import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import axiosInstance from './api/axios';
import Header from './Header'; 
import { useAuth } from './AuthContext'; 
import { FaArrowLeft } from 'react-icons/fa';

// --- Hardcoded Data ---
const generalInstructions = [
    { term: 'File format', description: 'All data delivered in .csv format' },
    { term: 'Character set', description: 'UTF-8 character set' },
    { term: 'Header row', description: 'Must have. Contains the field headers as defined in each interface specification. The header must be lower case.' },
    { term: 'Field order', description: 'The order of the fields/columns does not matter.' },
    { term: 'Additional data fields', description: 'The interface specific tables include the most common fields used in Plan-Relex Makeintegrations. Additional data can be provided to Relex Makeby using JSON \\"attribute fields, e.g. additional_item_ids on Items interface. The field itself is populated in Plan with a business rule and TQL from any fields connected to Product level.' },
    { term: 'File naming', description: 'Structure: {interface name}_{optional scope}_{content date}_{unique id}.csv, e.g. transactions_stores_2026-05-03_0001.csv\nThe interface name must be lower case; the scope is optional and may be upper or mixed case (it carries the source system\'s own casing); the unique id must be a number. The date must be referring to the extraction date from Plan.' },
    { term: 'CSV format definition', description: 'Each row must contain the same number of fields\nField delimiter is comma (,)\nIf field value contains line-break (LF/CR), the field must be wrapped with double quotes (\\") (UTF-8: U+0022), otherwise line-break will start a new row\nIf field value contains the field delimiter (comma (,)), the field must be wrapped with double quotes (\\"), otherwise it starts a new field\nIf field value contains a double quote character (\\"), the field must be wrapped with double quotes (\\"), and the double quote char represented by two double quote characters (\\"\\")\nSlanted double quotes (“) are treated like normal characters and do not need to be escaped\nIf field value contains backslash (\\\\), the field must be wrapped with double quotes (\\"), and the backslash character represented by two backslashes (\\\\\\\\)\nExample row: 123,Nail product group,\\"Galvanized 3\\"\\" nail\\",Additional details, contact info\\" 54.21' }
];
const supplyChainGlossary = [{ term: 'CCM', description: 'Customer Centric Merchandising. RELEX desktop application for planogram viewing, editing and automation. The modules of the application include CCM Space Planning (planogram viewer and editor), CCM Space Automation (database UI and automation work package setup), and CCM Processor (a service that actually creates automated planograms).' }, { term: 'DC', description: 'Distribution Center, sometimes also Data Centre (Private Cloud Infrastructure)' }, { term: 'Fastorm', description: 'The database that is built at RELEX which is at the core of RELEX Planning Cloud.' }, { term: 'MRP', description: 'Material Requirements Planning' }, { term: 'CO-MRP', description: 'Coordinated ordering MRP' }, { term: 'S&A', description: 'Space and Assortment product suite & teams working on these solutions' }, { term: 'SaaS', description: 'Software as a service (SaaS) is a software licensing and delivery model in which software is licensed on a subscription basis and is centrally hosted.' }, { term: 'Sampo', description: 'Best practice RELEX planning cloud environment. Also a wiki with that name exists for the same purpose: Introduction to SAMPO Best Practices. In Finnish mythology, the Sampo was a magical artifact of indeterminate type constructed by Ilmarinen that brought riches and good fortune to its holder. A popular first name in Finland' }, { term: 'TPM', description: 'Technical Project Management / Manager' }, { term: 'WAT', description: 'Web application team' }, { term: 'AM', description: 'Account Management / Manager' }, { term: 'PM', description: 'Project Management / Manager' }, { term: 'SM', description: 'Service Management / Manager' }, { term: 'TST', description: 'Technical Service Team' }, { term: 'FMCG', description: 'Fast Moving Consumer Goods' }, { term: 'EPOS', description: 'Electronic Point of Sale' }, { term: 'ERP', description: 'Enterprise Resource Planning' }, { term: 'EXP', description: 'Exponential smoothing forecast model: http://en.wikipedia.org/wiki/Exponential_smoothing' }, { term: 'MRP', description: 'Material Requirements Planning, an order model that considers forecasted future demand in the calculating order proposals: http://en.wikipedia.org/wiki/Material_requirements_planning' }, { term: 'CO-MRP', description: 'Coordinated ordering MRP' }, { term: 'ROP', description: 'Re-Order Point, an order model that places an order when balance falls at or below a pre-defined level, called the re-order point: http://en.wikipedia.org/wiki/Reorder_point' }, { term: 'TQL, tequila', description: 'A query language for querying Fastorm, also used for defining the \'formula\' for all metrics: https://relexsolutions.atlassian.net/wiki/display/SCP/Tql' }, { term: 'Availability', description: 'A metric that indicates how well a product was \'available\', measured e.g. as the proportion of stock-out days to all days' }, { term: 'ABC/XYZ analysis', description: 'An optimizer action that classifies products based on a metric, for example sales value. See https://relexsolutions.atlassian.net/wiki/display/SCP/AbcXyz' }, { term: 'Column dimension', description: 'A dimension selected to be shown on the rows of a report view in workspace' }, { term: 'Column header', description: 'A header for a column in a report view' }, { term: 'Cube engine', description: 'The part of our software that handles queries in the cube data model' }, { term: 'Cube', description: 'The abstraction used on top of the relational data model to represent data' }, { term: 'Customer config', description: 'A Ruby file that defines various configuration options for a particular customer' }, { term: 'Dimension', description: 'A dimension in the cube data model, e.g. products, locations, suppliers, days, weeks' }, { term: 'Export, processor export', description: 'A beast in the old version of our software that copies data between tables. Not needed in the new version.' }, { term: 'Fastorm', description: 'A custom database engine that\'s at the core of the new version, written in Java: https://relexsolutions.atlassian.net/wiki/display/SCP/Fastorm' }, { term: 'Fastor', description: 'A collection of C extensions to Postgres, used in the old version to facilitate better performance in handling time series data' }, { term: 'Field dimension, parameter dimension', description: 'An \'artificial\' dimension that lists all distinct values for a particular parameter in DB, allowing user to view aggregates for each. Can be selected to be shown on rows or columns of a report view, similarly to the \'real\' dimensions.' }, { term: 'Forecast model', description: 'An algorithm for forecasting future demand based on historical sales' }, { term: 'Forecast parameters', description: 'Set of parameters used by a forecast model in the forecasting process' }, { term: 'Home view', description: 'A popup that can be opened in the UI by clicking e.g. a product displayed on rows of a report view, showing details of that product' }, { term: 'Kernel', description: 'The part of our software that performs nightly runs, written in java' }, { term: 'Messages readers', description: 'Code that reads in incoming master data message files from customer\'s ERP system (written in Ruby)' }, { term: 'Metric', description: 'Some kind of measure computed from the (historical) data in the DB, e.g. sales or availability' }, { term: 'Nightly run', description: 'Batch job that creates sales forecasts and usually also order proposals' }, { term: 'Optimizer', description: 'The part of our software that can be used to automatically set or optimizer parameters that control the replenishment or forecasting process' }, { term: 'Order model', description: 'An algorithm used for calculating order proposals, e.g. MRP or ROP' }, { term: 'Order parameter', description: 'A parameter used by an order model in calculating order proposals' }, { term: 'Parameter', description: 'An attribute of some object, such as a product or product-location, that controls the replenishment or some other aspect' }, { term: 'PTO', description: 'Purchase To Order is an order model which can be used for products that are not stocked but only ordered when a sale or open sale transaction is entered into the system.' }, { term: 'Renderer', description: 'A Ruby file that describes, using a simple DSL, parameters of a particular table that users can view and manipulate in the workspace through the report views' }, { term: 'Row dimension', description: 'The dimension selected to be shown on the column of a report view in workspace' }, { term: 'Row header', description: 'A header for a row in a report view' }, { term: 'Transaction readers', description: 'Code that reads in incoming transaction data (written in Java)' }, { term: 'Values', description: 'The metrics & parameters that user wants to see in a report view in workspace' }, { term: 'Working copy', description: 'An isolated virtual \'copy\' of the DB, inside which all user changes take place. Can be accepted or rejected later.' }, { term: 'Workspace', description: 'A tab in the user interface where user can e.g. view reports and change parameters' }, { term: 'MAD', description: 'Mean Absolute Deviation' }, { term: 'MAPE', description: 'Mean Absolute Percentage Error' }, { term: 'WAPE', description: 'Weighted Absolute Percentage Error' }];
const dataTypesData = [
    { type: 'STRING', description: 'STRING of maximum length of 4096 characters. Fields in STRING format can be identified with quotation marks ("") as STRING qualifier.' },
    { type: 'STRING (ENUM)', description: 'STRING field with a set of accepted values that have to be exactly as specified to pass the validation. Must follow SCREAMING_SNAKE_CASE formatting' },
    { type: 'DATE', description: 'Date format is YYYY-MM-DD corresponding to ISO 8601 standard.' },
    { type: 'TIME', description: 'Time of day in hh:mm format (24 hour clock)' },
    { type: 'TIMESTAMP', description: 'Timestamp format is YYYY-MM-DDTHH:MM:SS+/-timenumoffset corresponding to RFC 3339 standard. E.g. 2020-02-20T20:02:20+02:00 stands for year 2020, 20th of February, time 20:02:20 in UTC+02:00 timezone.' },
    { type: 'INTEGER', description: 'Integer number.' },
    { type: 'LONG', description: 'Long integer number.' },
    { type: 'DOUBLE', description: 'Decimal (or INT) values. Decimal separator is decimal point ".". Used also for percentage values, in which case percentage value 20% is sent as 0.2"' },
    { type: 'BOOLEAN', description: 'Boolean value. true or false - Must be lower case' }
];
const transactionQuantitySignData = [
    { transaction: 'Quantity sign', sign: 'N/A', description: 'The quantity field for transactions must have a specific sign, depending on whether the transaction is inbound (positive sign, increasing balance) or outbound (negative sign, decreasing balance).' },
    { transaction: 'SALE, LOST_SALE', sign: 'Negative (-)', description: "Negative sign, unless it's return to the location" },
    { transaction: 'DELIVERY, SPECIAL_DELIVERY', sign: 'Positive (+)', description: "Positive sign, unless it's return delivery from the location" },
    { transaction: 'TRANSFER', sign: 'Positive/Negative (+/-)', description: 'Negative sign for the sending location (transfer from the location), positive sign for the receiving location (transfer in to the location)' },
    { transaction: 'SPOILAGE', sign: 'Negative (-)', description: 'Negative sign' },
    { transaction: 'ADJUSTMENT, INVENTORY_CHECK', sign: 'Positive/Negative (+/-)', description: 'Negative or positive sign depending on whether the adjustment is downwards (-) or upwards (+) compared to the previous level' },
    { transaction: 'ORDER', sign: 'Positive (+)', description: 'Positive sign' },
    { transaction: 'SALES_ORDER', sign: 'Negative (-)', description: 'Negative sign' }
];
const unitsOfMeasureData = [
    { unit: 'Inventory unit', description: 'Defines the base unit of measure of a product. Examples: pieces (pcs), SKUs, Kilograms (kg). Product-level' },
    { unit: 'inventory unit in consumer units', description: 'Determines what one unit for this product-location means in consumer package units. Used in cases where DC product has different base unit of measure, and a transformation is needed between store and DC. Product-location-level' },
    { unit: 'Units for Height, Width and Depth', description: 'All measurements must have the same unit across all the fields. RELEX Space systems have centimeters and inches available as UI options.' },
    { unit: 'Space unit', description: 'To define the product space unit used in coordinated ordering e.g. pallet places.\nMust be ensured that all products in one coordination group / supplier-location have same space unit when space is used as coordination limit. Product and product-location-level.' },
    { unit: 'Weight unit', description: 'To define the product weight unit used in coordinated ordering e.g. KG, lb.\nMust be ensured that all products in one coordination group / supplier-location have same weight when weight is used as coordination limit. Product and product-location-level.' },
    { unit: 'Volume unit', description: 'To define the product volume unit used in coordinated ordering e.g. M3, l, Gal.\nMust be ensured that all products in one coordination group / supplier-location have same volume unit when volume is used as coordination limit. Product and product-location-level.' }
];
const commonDiscrepanciesData = [
    { term: 'Incorrect or missing delimiter', description: 'The mandatory delimiter is missing or wrong.\nExample: Only ";" and "," are accepted delimiters.' },
    { term: 'Incorrect file name', description: 'The file contains some inconsistency in its name.\nExample: products_2024-01-01.' },
    { term: 'Non CSV format', description: 'The file format is not a valid CSV.\nExample: Only the .csv format is accepted.' },
    { term: 'Incorrect field type', description: 'A field contains a data type that does not match the specification.\nExample: Field type can be BOOLEAN, STRING, DATE, etc.' },
    { term: 'Incorrect field format', description: 'A field has the wrong format.\nExample: A date field is formatted as DD/MM/YYYY instead of the required YYYY-MM-DD.' },
    { term: 'Incorrect signal', description: 'A numeric value has the wrong sign (e.g., positive instead of negative).\nExample: Sales quantities should typically be negative (+ or -).' },
    { term: 'RELEX Pre-set', description: 'Fields already configured in the system must be given exactly the same format and/or content.\nExample: Transaction types must be one of "ORDER", "SALES_ORDER", etc.' },
    { term: 'Missing or wrong column name', description: 'One or more required column headers are missing or misspelled in the file.' },
    { term: 'Missing column', description: 'A column that is defined in the interface specification is completely missing from the file.' },
    { term: 'Missing values in mandatory fields', description: 'A mandatory column contains empty or null values in one or more rows.' },
    { term: 'Extra column', description: 'The file contains a column that is not part of the interface specification.' },
    { term: 'Duplicated row', description: 'The file contains one or more rows that are exact duplicates of another row where uniqueness is required.' },
    { term: 'Lack of combinations between interfaces', description: 'There is some discrepancy in content between interfaces (referential integrity).\nExample: The product-location file has duplicate entries for the same product and location combination.' },
    { term: 'Wrong content', description: 'The content in some or all of the fields is wrong.\nExample: Sales data in the file does not match the source ERP system.' }
];
const allInterfaceSummaries = [
    { name: 'Historical balances', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical sales', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical deliveries', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical spoilages', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical transfers', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical inventory checks', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical adjustments', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical lost sales', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 3 years' },
    { name: 'Historical campaigns', type: 'Historical file', subType: 'Master data', cycle: 'Once, batch upload', recommended: 'Full, same time period as SALES' },
    { name: 'Historical product-location-campaigns', type: 'Historical file', subType: 'Master data', cycle: 'Once, batch upload', recommended: 'Full, same time period as SALES' },
    { name: 'Historical Price calendars', type: 'Historical file', subType: 'Master data', cycle: 'Once, batch upload', recommended: 'Full, only current and future calendars' },
    { name: 'Historical Sales prices', type: 'Historical file', subType: 'Master data', cycle: 'Once, batch upload', recommended: 'Full, only current and future regular sales prices for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Historical Purchase prices', type: 'Historical file', subType: 'Master data', cycle: 'Once, batch upload', recommended: 'Full, only current and future regular purchase prices for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Historical Supplier fundings', type: 'Historical file', subType: 'Master data', cycle: 'Once, batch upload', recommended: 'Full, only current and future supplier funding for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Historical promotion assortments', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future assortment for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Merchandising product groups', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Sales', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Locations', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product groups', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product-locations', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Products', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Promotion assortments', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future assortment for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Campaigns', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future campaigns' },
    { name: 'Price calendars', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future calendars' },
    { name: 'Product-location price calendars', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future calendars' },
    { name: 'Sales prices', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future regular sales prices for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Purchase prices', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future regular purchase prices for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Competitor prices', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and past competitor prices for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Competitor campaigns', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and past competitor prices for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Product-location-campaigns', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future campaigns' },
    { name: 'Location hierarchy', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Opening times', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future dates' },
    { name: 'Assortments', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product-location-category-assortment', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'End customers', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product-end customers', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'End customer transactions', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Product Images', type: 'Other', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, then new images only' },
    { name: 'Floorplan and planogram migrations', type: 'One-off migration', subType: 'Master data', cycle: 'Once', recommended: 'Full' },
    { name: 'Balances', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Batch balances', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Open purchase orders', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'All open purchase orders' },
    { name: 'Open sales orders', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'All open sales orders' },
    { name: 'Deliveries', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Spoilages', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Transfers', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Inventory checks', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Adjustments', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Lost sales', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Delivery schedules', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future schedules' },
    { name: 'Product-batch sizes', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product-location-batch sizes', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product-location-suppliers', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Suppliers', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Supplier fundings', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future supplier funding for all products relevant for price optimization (Pricing) and promotion planning and optimization (Promotions)' },
    { name: 'Merchandising product catalogues', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Floor-plans', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Location-category-spaces', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Planogram-positions', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product-location-supplier-replenishment agreements', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Replenishment agreements', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product-location coordination groups', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Coordination groups', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Constraint groups', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Supplier-locations', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Brackets', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Supplier-location brackets', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Coordination group brackets', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Product replacements and refs', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Prepack variants', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Supplier closures', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full, only current and future dates' },
    { name: 'Category-clusters', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Output Order Proposals', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data' },
    { name: 'Output Reserve Order Proposals', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data' },
    { name: 'Output Sales Forecasts', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Historical footfalls', type: 'Historical file', subType: 'Transactions', cycle: 'Once, batch upload', recommended: 'Full, minimum 2 years' },
    { name: 'Footfalls', type: 'Inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data since last send' },
    { name: 'Bill-of-materials', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Barcodes', type: 'Inbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Output Clearance', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'New data' },
    { name: 'Output Workload driver forecast day level', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day / Every Week', recommended: 'Full' },
    { name: 'Output Workload driver forecast 15 min level', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day / Every Week', recommended: 'Full' },
    { name: 'Output Shelf Edge Labels', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day / Every Week', recommended: 'Full' },
    { name: 'Output Inventory exceptions', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'Delta' },
    { name: 'Output Promotions', type: 'Outbound interface', subType: 'Master data', cycle: 'Every Day', recommended: '' },
    { name: 'Output Prices', type: 'Outbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Output Assortments', type: 'Outbound interface', subType: 'Master data', cycle: 'Every Day', recommended: 'Full' },
    { name: 'DC transactions', type: 'Alternative inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'Full' },
    { name: 'Orders', type: 'Alternative inbound interface', subType: 'Transactions', cycle: 'Every Day', recommended: 'Delta' }
];
const dummyFiles = {
    balances: {
        name: 'balances_yyyy-MM-dd.csv',
        content: `date;location;product;type;balance;value\n2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;BALANCE;30;120\n2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;BALANCE;28;112\n2023-01-01;Loc D - Wysupp;Prod 3 - Wysupp;BALANCE;39;156`
    },
    campaigns: {
        name: 'campaigns_yyyy-MM-dd.csv',
        content: `campaign;name;start_date;end_date;campaign_class;category;type;custom_discount_type\nWY_CAMP01;WY_CAMP01;2023-02-18;2023-02-24;MARKDOWN;WY_CAMP01;WY_CAMP01;WY_CAMP01\nWY_CAMP02;WY_CAMP02;2023-03-25;2023-03-25;MARKDOWN;WY_CAMP02;WY_CAMP02;WY_CAMP02`
    },
    sales: {
        name: 'sales_yyyy-MM-dd.csv',
        content: `time;date;location;product;quantity;value\n08:00;2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;-14;98\n09:00;2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;-23;161\n10:00;2023-01-01;Loc D - Wysupp;Prod 3 - Wysupp;-23;161\n`
    },
    products: {
        name: 'products_yyyy-MM-dd.csv',
        content: `product;name;product_group;unit_of_measure;case_size;supplier;lifecycle_status\nProd 1 - Wysupp;Prod 1 - Wysupp;PRODGROUP1;PCS;1;Wysupp;ACTIVE\nProd 2 - Wysupp;Prod 2 - Wysupp;PRODGROUP2;PCS;1;Wysupp;ACTIVE\nProd 3 - Wysupp;Prod 3 - Wysupp;PRODGROUP3;PCS;1;Wysupp;ACTIVE\n`
    },
    product_locations: {
        name: 'product_locations_yyyy-MM-dd.csv',
        content: `product;location;sales_price;purchase_price\nProd 1 - Wysupp;Loc A - Wysupp;7;4\nProd 2 - Wysupp;Loc C - Wysupp;7;4\nProd 3 - Wysupp;Loc D - Wysupp;7;4\n`
    },
    product_groups: {
        name: 'product_groups_yyyy-MM-dd.csv',
        content: `code1;name1;code2;name2\nPG001;Grocery;PG001001;Pasta\nPG001;Grocery;PG001002;Meatball\nPG001;Grocery;PG001003;Flour\n`
    },
    locations: {
        name: 'locations_yyyy-MM-dd.csv',
        content: `location;name;type;chain;country\nLoc A - Wysupp;Loc A - Wysupp;STORE;Wysupp Chain;USA\nLoc C - Wysupp;Loc C - Wysupp;STORE;Wysupp Chain;USA\nLoc D - Wysupp;Loc D - Wysupp;STORE;Wysupp Chain;USA\nLoc E - Wysupp;Loc E - Wysupp;STORE;Wysupp Chain;USA\nLoc F - Wysupp;Loc F - Wysupp;STORE;Wysupp Chain;USA\nLoc G - Wysupp;Loc G - Wysupp;STORE;Wysupp Chain;USA\n`
    },
    location_hierarchy: [
        {
            name: 'countries_yyyy-MM-dd.csv',
            content: `country_code;country_name
USA;United States
CAN;Canada`
        },
        {
            name: 'chains_yyyy-MM-dd.csv',
            content: `chain_id;chain_name
WY_CHAIN;Wysupp Chain`
        }
    ],
    coordination_groups: {
        name: 'coordination_groups_yyyy-MM-dd.csv',
        content: `coordination_group_id;coordination_group_name\nCOORD1;Coordination Group 1\n`
    },
    suppliers: {
        name: 'suppliers_yyyy-MM-dd.csv',
        content: `supplier;name\nWysupp;Wysupp\n`
    },
    delivery_schedules: {
        name: 'delivery_schedules_yyyy-MM-dd.csv',
        content: `supplier;location;delivery_schedule;delivery_time;order_0;order_1;order_2;order_3;order_4;order_5;order_6;delivery_0;delivery_1;delivery_2;delivery_3;delivery_4;delivery_5;delivery_6;delivery_all
L180;L043;EVERY_WEEK;SEVEN_DAYS_WEEK;;;;;;;;;1;1;1;1;1;1;1
`
    },
    location_campaigns: {
        name: 'location_campaigns_yyyy-MM-dd.csv',
        content: `location_campaign;start_date;end_date;campaign_class;type;subtype;name
NATAL2024;2024-12-25;2024-12-25;EVENT;NATAL;NACIONAL;NATAL_2024
NATAL2025;2025-12-25;2025-12-25;EVENT;NATAL;NACIONAL;NATAL_2025
CPG0003;2025-01-01;2025-01-01;EVENT;ANO_NOVO;NACIONAL;ANO_NOVO_2025
`
    }, /* location_campaigns nao e uma interface padrao */
    supplier_locations: {
        name: 'supplier_locations_yyyy-MM-dd.csv',
        content: `supplier;location;boxes_min;boxes_max;pallets_min;pallets_max
SP001;LJ001;;;1;10
SP002;LJ002;;;1;35
`
    },
    product_location_supplier_replenishment_agreements: {
        name: 'product_location_supplier_replenishment_agreements_yyyy-MM-dd.csv',
        content: `product;location;supplier;replenishment_agreement
4267534;L220;SUPPLIER1;CONTRATO1
4267534;L220;SUPPLIER2;CONTRATO2
`
    },
    product_location_suppliers: {
        name: 'product_location_suppliers_yyyy-MM-dd.csv',
        content: `product;location;supplier;batch_size
4267534;L220;SUPPLIER1;10
4267534;L220;SUPPLIER2;5
`
    },
    replenishment_agreements: {
        name: 'replenishment_agreements_yyyy-MM-dd.csv',
        content: `replenishment_agreement;name;type;split_percentage;split_percentage_or_quota_unit;active;start_date;end_date
CONTRATO1;0,5;SUPPLIER1;SPLIT_PERCENTAGE;0,5;QUANTITY;true;2025-01-01;2025-12-31
CONTRATO2;0,5;SUPPLIER2;SPLIT_PERCENTAGE;0,5;QUANTITY;true;2025-01-01;2025-12-31
`
    },
    product_location_coordination_groups: {
        name: 'product_location_coordination_groups_yyyy-MM-dd.csv',
        content: `product;location;coordination_group
Prod 1 - Wysupp;Loc A - Wysupp;coordination_group_001
Prod 1 - Wysupp;Loc C - Wysupp;coordination_group_002
Prod 1 - Wysupp;Loc D - Wysupp;coordination_group_001
Prod 2 - Wysupp;Loc E - Wysupp;coordination_group_001
Prod 2 - Wysupp;Loc F - Wysupp;coordination_group_002
`
    },
    opening_times: {
        name: 'opening_times_yyyy-MM-dd.csv',
        content: `location;date;opening_time;closing_time
    Loc A - Wysupp;2023-01-01;08:00;22:00
    Loc C - Wysupp;2023-01-01;08:00;22:00
    Loc D - Wysupp;2023-01-01;08:00;22:00`
    },
    product_batch_sizes: {
        name: 'product_batch_sizes_yyyy-MM-dd.csv',
        content: `product;batch_size
    Prod 1 - Wysupp;12
    Prod 2 - Wysupp;24
    Prod 3 - Wysupp;36`
    },
    product_location_batch_sizes: {
        name: 'product_location_batch_sizes_yyyy-MM-dd.csv',
        content: `product;location;batch_size
    Prod 1 - Wysupp;Loc A - Wysupp;6
    Prod 2 - Wysupp;Loc C - Wysupp;12
    Prod 3 - Wysupp;Loc D - Wysupp;18`
    },
    end_customers: {
        name: 'end_customers_yyyy-MM-dd.csv',
        content: `end_customer;name;customer_group;chain
    CUST001;John Doe;RETAIL;Wysupp Chain
    CUST002;Jane Smith;WHOLESALE;Wysupp Chain
    CUST003;Store XYZ;RETAIL;Wysupp Chain`
    },
    product_end_customers: {
        name: 'product_end_customers_yyyy-MM-dd.csv',
        content: `product;end_customer;location
    Prod 1 - Wysupp;CUST001;Loc A - Wysupp
    Prod 2 - Wysupp;CUST002;Loc C - Wysupp
    Prod 3 - Wysupp;CUST003;Loc D - Wysupp`
    },
    end_customer_transactions: {
        name: 'end_customer_transactions_yyyy-MM-dd.csv',
        content: `time;date;location;product;end_customer;quantity;value
    08:00;2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;CUST001;-14;98
    09:00;2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;CUST002;-23;161
    10:00;2023-01-01;Loc D - Wysupp;Prod 3 - Wysupp;CUST003;-23;161`
    },
    open_sales_orders: {
        name: 'open_sales_orders_yyyy-MM-dd.csv',
        content: `sales_order;product;location;quantity;value;due_date
    ORDER001;Prod 1 - Wysupp;Loc A - Wysupp;10;70;2023-02-01
    ORDER002;Prod 2 - Wysupp;Loc C - Wysupp;15;105;2023-02-03
    ORDER003;Prod 3 - Wysupp;Loc D - Wysupp;20;140;2023-02-05`
    },
    product_replacements_and_refs: {
        name: 'manual_product_replacements_yyyy-MM-dd.csv',
        content: `product;location;replaced_product;replacement_product;start_date;end_date
    Prod A;Loc 1;Prod A;Prod B;2025-01-01;2025-12-31`
    },
    price_calendars: {
        name: 'price_calendars_yyyy-MM-dd.csv',
        content: `price_calendar;name;start_date;end_date
    CALENDAR1;Standard Prices 2025;2025-01-01;2025-12-31
    CALENDAR2;Summer Sale;2025-06-01;2025-08-31`
    },
    product_location_price_calendars: {
        name: 'product_location_price_calendars_yyyy-MM-dd.csv',
        content: `product;location;price_calendar
    Prod 1 - Wysupp;Loc A - Wysupp;CALENDAR1
    Prod 2 - Wysupp;Loc C - Wysupp;CALENDAR2`
    },
    deliveries: {
        name: 'deliveries_yyyy-MM-dd.csv',
        content: `time;date;location;product;quantity;value
    08:00;2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;50;200
    09:00;2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;40;160
    10:00;2023-01-01;Loc D - Wysupp;Prod 3 - Wysupp;60;240`
    },
    spoilages: {
        name: 'spoilages_yyyy-MM-dd.csv',
        content: `time;date;location;product;quantity;value
    18:00;2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;-2;8
    18:05;2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;-1;4`
    },
    inventory_checks: {
        name: 'inventory_checks_yyyy-MM-dd.csv',
        content: `time;date;location;product;quantity;value
    07:00;2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;5;20
    07:05;2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;-2;8`
    },
    adjustments: {
        name: 'adjustments_yyyy-MM-dd.csv',
        content: `time;date;location;product;quantity;value
    10:30;2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;1;4
    11:00;2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;-1;-4`
    },
    lost_sales: {
        name: 'lost_sales_yyyy-MM-dd.csv',
        content: `time;date;location;product;quantity;value
    14:00;2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;-5;35
    15:00;2023-01-01;Loc C - Wysupp;Prod 2 - Wysupp;-10;70`
    },
    transfers: {
        name: 'transfers_yyyy-MM-dd.csv',
        content: `time;date;from_location;to_location;product;quantity;value
    12:00;2023-01-01;Loc A - Wysupp;Loc D - Wysupp;Prod 1 - Wysupp;-10;-40
    12:00;2023-01-01;Loc D - Wysupp;Loc A - Wysupp;Prod 1 - Wysupp;10;40`
    },
    batch_balances: {
        name: 'batch_balances_yyyy-MM-dd.csv',
        content: `date;location;product;batch;balance;value;expiry_date
    2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;B001;15;60;2023-06-30
    2023-01-01;Loc A - Wysupp;Prod 1 - Wysupp;B002;15;60;2023-07-31`
    },
    open_purchase_orders: {
        name: 'open_purchase_orders_yyyy-MM-dd.csv',
        content: `purchase_order;product;location;supplier;quantity;value;due_date
    PO001;Prod 1 - Wysupp;Loc A - Wysupp;Wysupp;100;400;2023-02-15
    PO002;Prod 2 - Wysupp;Loc C - Wysupp;Wysupp;150;600;2023-02-20`
    },
    bill_of_materials: {
        name: 'bill_of_materials_yyyy-MM-dd.csv',
        content: `end_product_code;component_product_code;end_location_code;component_location_code;quantity;start_date;end_date;active;yield_percentage;yield_quantity
    bread123;flour100;DC100;ST230;6;2025-09-01;2025-09-01;true;0,95;5
    bread124;flour101;DC101;ST231;3;2025-09-01;2025-09-01;true;0,75;4
    bread125;flour102;DC102;ST232;8;2025-09-01;2025-09-01;true;0,6;9`
    },
    merchandising_product_groups: {
        name: 'merchandising_product_groups_yyyy-MM-dd.csv',
        content: `merchandising_product_groups_1$code;merchandising_product_groups_1$name;merchandising_product_groups_1$type;merchandising_product_groups_1$active;merchandising_product_groups_2$code;merchandising_product_groups_2$name;merchandising_product_groups_2$type;merchandising_product_groups_2$active;merchandising_product_groups_3$code;merchandising_product_groups_3$name;merchandising_product_groups_3$type;merchandising_product_groups_3$active;merchandising_product_groups_4$code;merchandising_product_groups_4$name;merchandising_product_groups_4$type;merchandising_product_groups_4$active;merchandising_product_groups_5$code;merchandising_product_groups_5$name;merchandising_product_groups_5$type;merchandising_product_groups_5$active
    10;Grocery;Standard;true;1050;Dairy;Standard;true;1050010;Yogurt;Standard;true;;;;;;;;
    10;Grocery;Standard;true;1050;Dairy;Standard;false;1050024;Milk;Standard;true;;;;;;;;
    10;Grocery;Standard;true;1050;Dairy;Standard;true;1050038;Cheese;Standard;true;;;;;;;;
    10;Grocery;Standard;false;1050;Dairy;Standard;false;10...`
    },
    purchase_prices: {
        name: 'purchase_prices_yyyy-MM-dd.csv',
        content: `product;location;start_date;end_date;purchase_price;price_type
    P1000;L200;2025-09-01;2025-09-01;1.99;NORMAL
    P1001;L201;2025-09-02;2025-09-02;2.49;NORMAL
    P1002;L202;2025-09-03;2025-09-03;2.99;NORMAL
    P1003;L203;2025-09-04;2025-09-04;3.49;NORMAL
    P1004;L204;2025-09-05;2025-09-05;3.99;OTHER`
    },
    sales_prices: {
        name: 'sales_prices_yyyy-MM-dd.csv',
        content: `product;location;start_date;end_date;sales_price;sales_price_with_vat;price_type
    P1000;L200;2025-09-01;2025-09-01;1.99;1.99;NORMAL
    P1001;L201;2025-09-02;2025-09-02;2.49;2.49;NORMAL
    P1002;L202;2025-09-03;2025-09-03;2.99;2.99;NORMAL
    P1003;L203;2025-09-04;2025-09-04;3.49;3.49;NORMAL
    P1004;L204;2025-09-05;2025-09-05;3.99;3.99;OTHER`
    },
    barcodes: {
        name: 'barcodes_yyyy-MM-dd.csv',
        content: `ean;product;ean_batch_size_label;primary_ean
    4740050002116;58974;10101001;false
    4740050002117;47375;10101010;true
    4740050002118;38475;11001101;true
    4740050002119;92834;10101010;false
    4740050002120;88473;11001010;false`
    },
    competitor_campaigns: {
        name: 'competitor_campaigns_yyyy-MM-dd.csv',
        content: `campaign;name;start_date;end_date;campaign_class;category;campaign_group;campaign_group_name;minimum_purchase_quantity;maximum_purchase_quantity;reward_quantity;multiple_quantity;price_modification_method_code;price_modification_value;state
    val_0;name_0;2025-09-01;2025-09-01;val_0;val_0;val_0;campaign_group_name_0;1;1;1;1;1.99;1.99;val_0
    val_1;name_1;2025-09-02;2025-09-02;val_1;val_1;val_1;campaign_group_name_1;2;2;2;2;2.49;2.49;val_1`
    },
    footfalls: {
        name: 'footfalls_yyyy-MM-dd.csv',
        content: `date;time;location;quantity
    2025-09-01;08:00;L200;1
    2025-09-02;09:00;L201;2
    2025-09-03;10:00;L202;3
    2025-09-04;11:00;L203;4
    2025-09-05;12:00;L204;5`
    },
    dc_transactions: {
        name: 'dc_transactions_yyyy-MM-dd.csv',
        content: `date;product;location;type;sales_orders_value;sales_orders_quantity;balance;estimated_date;time
    2025-09-01;D100;58724;OPEN_SALES_ORDER;100;-1;-1;2025-09-01;08:00
    2025-09-02;D101;83745;OPEN_SALES_ORDER;238;-2;-2;2025-09-02;09:00`
    },
    merchandising_product_catalogues: {
        name: 'product_location_categories_yyyy-MM-dd.csv',
        content: `product;location;merchandising_product_group_x
    P1000;L200;1050
    P1001;L201;1050
    P1002;L202;1050
    P1003;L203;1050
    P1004;L204;1050`
    },
    orders: {
        name: 'orders_yyyy-MM-dd.csv',
        content: `date;location;product;quantity;requested_quantity;confirmed_date;delivered_quantity;reference;order_row;estimated_date;requested_date;estimated_delivery_time;time;confirmed_quantity;partner_code;order_type;value;delivered_date;status;expiration_date
    2025-09-01;L200;P1000;-1;-1;2025-09-01;-1;PO4233;5;2025-09-01;2025-09-01;08:00;08:00;-1;SU4320;DC_ORDER;200;2025-09-01;OPEN;2025-09-01
    2025-09-02;L201;P1001;-2;-2;2025-09-02;-2;PO4234;8;2025-09-02;2025-09-02;09:00;09:00;-2;SU4320;DC_ORDER;200;2025-09-02;CONFIRMED;2025-09-02`
    },
    prepack_variants: {
        name: 'prepack_variants_yyyy-MM-dd.csv',
        content: `pre_pack_product_code;variant_product_code;variant_quantity;state
P1000;P1010;1;DELETE
P1001;P1011;2;DELETE
P1002;P1012;3;DELETE
P1003;P1013;4;DELETE
P1004;P1014;5;DELETE`
    },
    planogram_positions: {
        name: 'planogram_positions_yyyy-MM-dd.csv',
        content: `planograms$code;planograms$name;planogram_assignments$code;planogram_assignments$start_date;planogram_assignments$end_date;planogram_assignments$status;planogram_assignments$is_active;bays$code;bays$sequence;components$code;components$name;components$type;components$sequence;positions$code;products$code;locations$code;positions$merchandising_product_group_x;positions$assortment;positions$sequence;positions$merchandising_style;positions$min_fill;positions$total_units;positions$units_deep;positions$units_high;positions$units_wide;positions$side_cap_deep;positions$side_cap_high;positions$side_cap_wide;positions$side_cap_total_units;positions$x_coordinate;positions$y_coordinate;positions$z_coordinate
1001;Milk;1001-550-2022-01-01;2022-01-01;2022-02-28;LIVE;true;B1001-2;256;C1001-4-B1001-2;Milk shelf;SHELF;123;POS57896-1;57896;550;105010;105040;489;TRAY;12.0;18;6;1;3;0;0;0;0;1.1;2.1;2.3`
    },
    promotion_assortments: {
        name: 'promotion_assortments_yyyy-MM-dd.csv',
        content: `product;location;start_date;end_date;is_listed
P1000;L200;2025-09-01;2025-09-01;true
P1001;L201;2025-09-02;2025-09-02;true
P1002;L202;2025-09-03;2025-09-03;true
P1003;L203;2025-09-04;2025-09-04;false
P1004;L204;2025-09-05;2025-09-05;false`
    },
    supplier_closures: [
        {
            name: 'supplier_closures_yyyy-MM-dd.csv',
            content: `supplier;date;closed
S050;2025-09-01;true
S051;2025-09-02;true
S052;2025-09-03;true
S053;2025-09-04;true
S054;2025-09-05;true`
        },
        {
            name: 'supplier_location_closures_yyyy-MM-dd.csv',
            content: `location;supplier;date;closed
L200;S050;2025-09-01;true
L201;S051;2025-09-02;true
L202;S052;2025-09-03;true
L203;S053;2025-09-04;true
L204;S054;2025-09-05;true`
        }
    ],
    supplier_fundings: {
        name: 'supplier_fundings_yyyy-MM-dd.csv',
        content: `supplier_funding;supplier;supplier_funding_type;supplier_funding_value;campaign_group;campaign;start_date;end_date;location;product;state
S050;A100;FIXED_AMOUNT;2000;CG258;C456;2025-09-01;2025-09-01;L200;P1000;DELETE
S051;A101;SHARE_OF_DISCOUNT_AMOUNT;3000;CG258;C456;2025-09-02;2025-09-02;L201;P1001;DELETE
S052;A102;PER_UNIT_AMOUNT;5500;CG258;C456;2025-09-03;2025-09-03;L202;P1002;
S053;A103;SHARE_OF_UNIT_COST;6100;CG258;C456;2025-09-04;2025-09-04;L203;P1003;
S054;A104;DISCOUNTED_UNIT_COST;3800;CG258;C456;2025-09-05;2025-09-05;L204;P1004;`
    },
    constraint_groups: [
        {
            name: 'constraint_groups_yyyy-MM-dd.csv',
            content: `code;active;name;constraint_date_type;level;multiple_constraints;multiples_max;trigger_level;priority;use_dynamic_limits;product;supplier;unit
CG_PALLET_Wysupp;true;Wysupp Pallet Group;ORDER_DATE;SUPPLY;true;5;0.75;100;false;;SUPP_A;PALLET_LAYERS
CG_WEIGHT_Wysupp;true;Wysupp Weight Limit;DELIVERY_DATE;SUPPLY;false;;;200;false;;SUPP_B;KG
CG_VOL_Wysupp;true;Wysupp Volume Group;ORDER_DATE;SUPPLY;true;3;0.5;150;true;Prod 1 - Wysupp;;M3
CG_MIN_Wysupp;false;Inactive Group;ORDER_DATE;SUPPLY;false;;;300;false;;;PCS`
        },
        {
            name: 'constraint_group_constraints_yyyy-MM-dd.csv',
            content: `constraint_group;date;static_minimum;static_maximum;exact;reserved_amount
CG_PALLET_Wysupp;2026-01-16;10.0;50.0;;5.0
CG_PALLET_Wysupp;2026-01-17;10.0;50.0;;2.0
CG_WEIGHT_Wysupp;2026-01-16;100.5;1000.0;;25.0
CG_VOL_Wysupp;2026-01-16;;25.5;15.0;0.0
CG_PALLET_Wysupp;2026-01-20;20.0;100.0;;10.0`
        },
        {
            name: 'product_location_constraint_groups_yyyy-MM-dd.csv',
            content: `product;location;constraint_group
Prod 1 - Wysupp;Loc A - Wysupp;CG_PALLET_Wysupp
Prod 2 - Wysupp;Loc A - Wysupp;CG_PALLET_Wysupp
Prod 3 - Wysupp;Loc B - Wysupp;CG_WEIGHT_Wysupp
Prod 1 - Wysupp;Loc B - Wysupp;CG_VOL_Wysupp
Prod 2 - Wysupp;Loc C - Wysupp;CG_PALLET_Wysupp`
        }
    ],
    brackets: {
        name: 'brackets_yyyy-MM-dd.csv',
        content: `bracket;bracket_limit;bracket_discount;bracket_type
BR_Wysupp_01;1000.0;0.03;CURRENT
BR_Wysupp_02;5000.0;0.05;CURRENT
BR_Wysupp_03;10000.0;0.07;NEXT`
    },
    supplier_location_brackets: {
        name: 'location_supplier_brackets_yyyy-MM-dd.csv',
        content: `bracket;location;supplier
BR_Wysupp_01;Loc A - Wysupp;SUPP_A
BR_Wysupp_02;Loc A - Wysupp;SUPP_A
BR_Wysupp_01;Loc B - Wysupp;SUPP_B
BR_Wysupp_03;Loc C - Wysupp;SUPP_C`
    },
    coordination_group_brackets: {
        name: 'coordination_group_brackets_yyyy-MM-dd.csv',
        content: `bracket;coordination_group
BR_Wysupp_01;CG_Wysupp_Main
BR_Wysupp_02;CG_Wysupp_Main
BR_Wysupp_03;CG_Wysupp_Global`
    },
    competitor_prices: {
        name: 'competitor_prices_yyyy-MM-dd.csv',
        content: `observed_at;product;competitor;location;location_attribute_name;location_attribute_value;type;sales_price
2024-01-01;Prod 1 - Wysupp;competitor1;Loc 1 - Wysupp;Price zone;Price zone ABC;REGULAR;2.99
2024-01-01;Prod 2 - Wysupp;competitor1;Loc 1 - Wysupp;Price zone;Price zone ABC;PROMOTED;1.99
2024-01-01;Prod 3 - Wysupp;competitor1;Loc 1 - Wysupp;Price zone;Price zone ABC;LOYALTY;1.99
2024-01-01;Prod 4 - Wysupp;competitor1;Loc 1 - Wysupp;Price zone;Price zone ABC;DC;1.99`
    },
    product_location_campaigns: {
        name: 'product_location_campaigns_yyyy-MM-dd.csv',
        content: `campaign;product;location;campaign_price;type;subtype
WY_CAMP01;Prod 1 - Wysupp;Loc E - Wysupp;5.5;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 1 - Wysupp;Loc F - Wysupp;5.5;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 1 - Wysupp;Loc G - Wysupp;5.5;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 1 - Wysupp;Loc H - Wysupp;5.5;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 2 - Wysupp;Loc E - Wysupp;16;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 2 - Wysupp;Loc F - Wysupp;16;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 2 - Wysupp;Loc G - Wysupp;16;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 2 - Wysupp;Loc H - Wysupp;16;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 3 - Wysupp;Loc E - Wysupp;5;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 3 - Wysupp;Loc F - Wysupp;5;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 3 - Wysupp;Loc G - Wysupp;5;WY_CAMP01;WY_CAMP01
WY_CAMP01;Prod 3 - Wysupp;Loc H - Wysupp;5;WY_CAMP01;WY_CAMP01
WY_CAMP02;Prod 1 - Wysupp;Loc E - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 1 - Wysupp;Loc F - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 1 - Wysupp;Loc G - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 1 - Wysupp;Loc H - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 2 - Wysupp;Loc E - Wysupp;16;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 2 - Wysupp;Loc F - Wysupp;16;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 2 - Wysupp;Loc G - Wysupp;16;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 2 - Wysupp;Loc H - Wysupp;16;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 3 - Wysupp;Loc E - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 3 - Wysupp;Loc F - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 3 - Wysupp;Loc G - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 3 - Wysupp;Loc H - Wysupp;5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 4 - Wysupp;Loc E - Wysupp;1.5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 4 - Wysupp;Loc F - Wysupp;1.5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 4 - Wysupp;Loc G - Wysupp;1.5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 4 - Wysupp;Loc H - Wysupp;1.5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 5 - Wysupp;Loc E - Wysupp;8.5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 5 - Wysupp;Loc F - Wysupp;8.5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 5 - Wysupp;Loc G - Wysupp;8.5;WY_CAMP02;WY_CAMP02
WY_CAMP02;Prod 5 - Wysupp;Loc H - Wysupp;8.5;WY_CAMP02;WY_CAMP02
WY_CAMP03;Prod 1 - Wysupp;Loc E - Wysupp;7;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 1 - Wysupp;Loc F - Wysupp;7;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 1 - Wysupp;Loc G - Wysupp;7;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 1 - Wysupp;Loc H - Wysupp;7;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 2 - Wysupp;Loc E - Wysupp;18;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 2 - Wysupp;Loc F - Wysupp;18;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 2 - Wysupp;Loc G - Wysupp;18;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 2 - Wysupp;Loc H - Wysupp;18;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 3 - Wysupp;Loc E - Wysupp;5.5;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 3 - Wysupp;Loc F - Wysupp;5.5;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 3 - Wysupp;Loc G - Wysupp;5.5;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 3 - Wysupp;Loc H - Wysupp;5.5;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 5 - Wysupp;Loc E - Wysupp;9;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 5 - Wysupp;Loc F - Wysupp;9;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 5 - Wysupp;Loc G - Wysupp;9;WY_CAMP03;WY_CAMP03
WY_CAMP03;Prod 5 - Wysupp;Loc H - Wysupp;9;WY_CAMP03;WY_CAMP03
WY_CAMP04;Prod 1 - Wysupp;Loc E - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 1 - Wysupp;Loc F - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 1 - Wysupp;Loc G - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 1 - Wysupp;Loc H - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 2 - Wysupp;Loc E - Wysupp;18;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 2 - Wysupp;Loc F - Wysupp;18;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 2 - Wysupp;Loc G - Wysupp;18;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 2 - Wysupp;Loc H - Wysupp;18;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 3 - Wysupp;Loc E - Wysupp;5.5;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 3 - Wysupp;Loc F - Wysupp;5.5;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 3 - Wysupp;Loc G - Wysupp;5.5;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 3 - Wysupp;Loc H - Wysupp;5.5;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 5 - Wysupp;Loc E - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 5 - Wysupp;Loc F - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 5 - Wysupp;Loc G - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP04;Prod 5 - Wysupp;Loc H - Wysupp;9;WY_CAMP04;WY_CAMP04
WY_CAMP05;Prod 1 - Wysupp;Loc E - Wysupp;7;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 1 - Wysupp;Loc F - Wysupp;7;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 1 - Wysupp;Loc G - Wysupp;7;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 1 - Wysupp;Loc H - Wysupp;7;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 2 - Wysupp;Loc E - Wysupp;18;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 2 - Wysupp;Loc F - Wysupp;18;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 2 - Wysupp;Loc G - Wysupp;18;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 2 - Wysupp;Loc H - Wysupp;18;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 3 - Wysupp;Loc E - Wysupp;5.5;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 3 - Wysupp;Loc F - Wysupp;5.5;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 3 - Wysupp;Loc G - Wysupp;5.5;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 3 - Wysupp;Loc H - Wysupp;5.5;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 5 - Wysupp;Loc E - Wysupp;9;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 5 - Wysupp;Loc F - Wysupp;9;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 5 - Wysupp;Loc G - Wysupp;9;WY_CAMP05;WY_CAMP05
WY_CAMP05;Prod 5 - Wysupp;Loc H - Wysupp;9;WY_CAMP05;WY_CAMP05
WY_CAMP06;Prod 1 - Wysupp;Loc E - Wysupp;7;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 1 - Wysupp;Loc F - Wysupp;7;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 1 - Wysupp;Loc G - Wysupp;7;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 1 - Wysupp;Loc H - Wysupp;7;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 2 - Wysupp;Loc E - Wysupp;18;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 2 - Wysupp;Loc F - Wysupp;18;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 2 - Wysupp;Loc G - Wysupp;18;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 2 - Wysupp;Loc H - Wysupp;18;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 3 - Wysupp;Loc E - Wysupp;5.5;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 3 - Wysupp;Loc F - Wysupp;5.5;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 3 - Wysupp;Loc G - Wysupp;5.5;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 3 - Wysupp;Loc H - Wysupp;5.5;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 5 - Wysupp;Loc E - Wysupp;9;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 5 - Wysupp;Loc F - Wysupp;9;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 5 - Wysupp;Loc G - Wysupp;9;WY_CAMP06;WY_CAMP06
WY_CAMP06;Prod 5 - Wysupp;Loc H - Wysupp;9;WY_CAMP06;WY_CAMP06
WY_CAMP07;Prod 1 - Wysupp;Loc E - Wysupp;7;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 1 - Wysupp;Loc F - Wysupp;7;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 1 - Wysupp;Loc G - Wysupp;7;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 1 - Wysupp;Loc H - Wysupp;7;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 2 - Wysupp;Loc E - Wysupp;18;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 2 - Wysupp;Loc F - Wysupp;18;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 2 - Wysupp;Loc G - Wysupp;18;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 2 - Wysupp;Loc H - Wysupp;18;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 3 - Wysupp;Loc E - Wysupp;5.5;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 3 - Wysupp;Loc F - Wysupp;5.5;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 3 - Wysupp;Loc G - Wysupp;5.5;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 3 - Wysupp;Loc H - Wysupp;5.5;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 5 - Wysupp;Loc E - Wysupp;9;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 5 - Wysupp;Loc F - Wysupp;9;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 5 - Wysupp;Loc G - Wysupp;9;WY_CAMP07;WY_CAMP07
WY_CAMP07;Prod 5 - Wysupp;Loc H - Wysupp;9;WY_CAMP07;WY_CAMP07
WY_CAMP08;Prod 1 - Wysupp;Loc E - Wysupp;8;WY_CAMP08;WY_CAMP08
WY_CAMP08;Prod 1 - Wysupp;Loc F - Wysupp;8;WY_CAMP08;WY_CAMP08
WY_CAMP08;Prod 1 - Wysupp;Loc G - Wysupp;8;WY_CAMP08;WY_CAMP08
WY_CAMP08;Prod 1 - Wysupp;Loc H - Wysupp;8;WY_CAMP08;WY_CAMP08
WY_CAMP08;Prod 2 - Wysupp;Loc E - Wysupp;19.5;WY_CAMP08;WY_CAMP08
WY_CAMP08;Prod 2 - Wysupp;Loc F - Wysupp;19.5;WY_CAMP08;WY_CAMP08
WY_CAMP08;Prod 2 - Wysupp;Loc G - Wysupp;19.5;WY_CAMP08;WY_CAMP08
WY_CAMP08;Prod 2 - Wysupp;Loc H - Wysupp;19.5;WY_CAMP08;WY_CAMP08
WY_CAMP09;Prod 3 - Wysupp;Loc E - Wysupp;6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 3 - Wysupp;Loc F - Wysupp;6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 3 - Wysupp;Loc G - Wysupp;6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 3 - Wysupp;Loc H - Wysupp;6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 4 - Wysupp;Loc E - Wysupp;1.6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 4 - Wysupp;Loc F - Wysupp;1.6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 4 - Wysupp;Loc G - Wysupp;1.6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 4 - Wysupp;Loc H - Wysupp;1.6;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 5 - Wysupp;Loc E - Wysupp;10;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 5 - Wysupp;Loc F - Wysupp;10;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 5 - Wysupp;Loc G - Wysupp;10;WY_CAMP09;WY_CAMP09
WY_CAMP09;Prod 5 - Wysupp;Loc H - Wysupp;10;WY_CAMP09;WY_CAMP09
WY_CAMP10;Prod 1 - Wysupp;Loc E - Wysupp;8;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 1 - Wysupp;Loc F - Wysupp;8;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 1 - Wysupp;Loc G - Wysupp;8;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 1 - Wysupp;Loc H - Wysupp;8;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 2 - Wysupp;Loc E - Wysupp;19.5;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 2 - Wysupp;Loc F - Wysupp;19.5;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 2 - Wysupp;Loc G - Wysupp;19.5;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 2 - Wysupp;Loc H - Wysupp;19.5;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 3 - Wysupp;Loc E - Wysupp;6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 3 - Wysupp;Loc F - Wysupp;6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 3 - Wysupp;Loc G - Wysupp;6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 3 - Wysupp;Loc H - Wysupp;6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 4 - Wysupp;Loc E - Wysupp;1.6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 4 - Wysupp;Loc F - Wysupp;1.6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 4 - Wysupp;Loc G - Wysupp;1.6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 4 - Wysupp;Loc H - Wysupp;1.6;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 5 - Wysupp;Loc E - Wysupp;10;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 5 - Wysupp;Loc F - Wysupp;10;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 5 - Wysupp;Loc G - Wysupp;10;WY_CAMP10;WY_CAMP10
WY_CAMP10;Prod 5 - Wysupp;Loc H - Wysupp;10;WY_CAMP10;WY_CAMP10
WY_CAMP11;Prod 3 - Wysupp;Loc E - Wysupp;6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 3 - Wysupp;Loc F - Wysupp;6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 3 - Wysupp;Loc G - Wysupp;6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 3 - Wysupp;Loc H - Wysupp;6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 4 - Wysupp;Loc E - Wysupp;1.6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 4 - Wysupp;Loc F - Wysupp;1.6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 4 - Wysupp;Loc G - Wysupp;1.6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 4 - Wysupp;Loc H - Wysupp;1.6;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 5 - Wysupp;Loc E - Wysupp;10;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 5 - Wysupp;Loc F - Wysupp;10;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 5 - Wysupp;Loc G - Wysupp;10;WY_CAMP11;WY_CAMP11
WY_CAMP11;Prod 5 - Wysupp;Loc H - Wysupp;10;WY_CAMP11;WY_CAMP11
WY_CAMP12;Prod 3 - Wysupp;Loc E - Wysupp;6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 3 - Wysupp;Loc F - Wysupp;6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 3 - Wysupp;Loc G - Wysupp;6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 3 - Wysupp;Loc H - Wysupp;6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 4 - Wysupp;Loc E - Wysupp;1.6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 4 - Wysupp;Loc F - Wysupp;1.6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 4 - Wysupp;Loc G - Wysupp;1.6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 4 - Wysupp;Loc H - Wysupp;1.6;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 5 - Wysupp;Loc E - Wysupp;10;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 5 - Wysupp;Loc F - Wysupp;10;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 5 - Wysupp;Loc G - Wysupp;10;WY_CAMP12;WY_CAMP12
WY_CAMP12;Prod 5 - Wysupp;Loc H - Wysupp;10;WY_CAMP12;WY_CAMP12
WY_CAMP13;Prod 1 - Wysupp;Loc E - Wysupp;10;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 1 - Wysupp;Loc F - Wysupp;10;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 1 - Wysupp;Loc G - Wysupp;10;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 1 - Wysupp;Loc H - Wysupp;10;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 2 - Wysupp;Loc E - Wysupp;20;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 2 - Wysupp;Loc F - Wysupp;20;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 2 - Wysupp;Loc G - Wysupp;20;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 2 - Wysupp;Loc H - Wysupp;20;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 3 - Wysupp;Loc E - Wysupp;7;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 3 - Wysupp;Loc F - Wysupp;7;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 3 - Wysupp;Loc G - Wysupp;7;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 3 - Wysupp;Loc H - Wysupp;7;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 4 - Wysupp;Loc E - Wysupp;1.8;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 4 - Wysupp;Loc F - Wysupp;1.8;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 4 - Wysupp;Loc G - Wysupp;1.8;WY_CAMP13;WY_CAMP13
WY_CAMP13;Prod 4 - Wysupp;Loc H - Wysupp;1.8;WY_CAMP13;WY_CAMP13
WY_CAMP14;Prod 1 - Wysupp;Loc E - Wysupp;10;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 1 - Wysupp;Loc F - Wysupp;10;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 1 - Wysupp;Loc G - Wysupp;10;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 1 - Wysupp;Loc H - Wysupp;10;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 2 - Wysupp;Loc E - Wysupp;20;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 2 - Wysupp;Loc F - Wysupp;20;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 2 - Wysupp;Loc G - Wysupp;20;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 2 - Wysupp;Loc H - Wysupp;20;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 3 - Wysupp;Loc E - Wysupp;7;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 3 - Wysupp;Loc F - Wysupp;7;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 3 - Wysupp;Loc G - Wysupp;7;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 3 - Wysupp;Loc H - Wysupp;7;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 4 - Wysupp;Loc E - Wysupp;1.8;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 4 - Wysupp;Loc F - Wysupp;1.8;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 4 - Wysupp;Loc G - Wysupp;1.8;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 4 - Wysupp;Loc H - Wysupp;1.8;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 5 - Wysupp;Loc E - Wysupp;11;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 5 - Wysupp;Loc F - Wysupp;11;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 5 - Wysupp;Loc G - Wysupp;11;WY_CAMP14;WY_CAMP14
WY_CAMP14;Prod 5 - Wysupp;Loc H - Wysupp;11;WY_CAMP14;WY_CAMP14
WY_CAMP15;Prod 1 - Wysupp;Loc E - Wysupp;10;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 1 - Wysupp;Loc F - Wysupp;10;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 1 - Wysupp;Loc G - Wysupp;10;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 1 - Wysupp;Loc H - Wysupp;10;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 2 - Wysupp;Loc E - Wysupp;20;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 2 - Wysupp;Loc F - Wysupp;20;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 2 - Wysupp;Loc G - Wysupp;20;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 2 - Wysupp;Loc H - Wysupp;20;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 3 - Wysupp;Loc E - Wysupp;7;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 3 - Wysupp;Loc F - Wysupp;7;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 3 - Wysupp;Loc G - Wysupp;7;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 3 - Wysupp;Loc H - Wysupp;7;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 4 - Wysupp;Loc E - Wysupp;1.8;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 4 - Wysupp;Loc F - Wysupp;1.8;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 4 - Wysupp;Loc G - Wysupp;1.8;WY_CAMP15;WY_CAMP15
WY_CAMP15;Prod 4 - Wysupp;Loc H - Wysupp;1.8;WY_CAMP15;WY_CAMP15
WY_CAMP16;Prod 3 - Wysupp;Loc E - Wysupp;7;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 3 - Wysupp;Loc F - Wysupp;7;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 3 - Wysupp;Loc G - Wysupp;7;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 3 - Wysupp;Loc H - Wysupp;7;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 4 - Wysupp;Loc E - Wysupp;1.8;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 4 - Wysupp;Loc F - Wysupp;1.8;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 4 - Wysupp;Loc G - Wysupp;1.8;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 4 - Wysupp;Loc H - Wysupp;1.8;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 5 - Wysupp;Loc E - Wysupp;11;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 5 - Wysupp;Loc F - Wysupp;11;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 5 - Wysupp;Loc G - Wysupp;11;WY_CAMP16;WY_CAMP16
WY_CAMP16;Prod 5 - Wysupp;Loc H - Wysupp;11;WY_CAMP16;WY_CAMP16
WY_CAMP17;Prod 1 - Wysupp;Loc E - Wysupp;10;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 1 - Wysupp;Loc F - Wysupp;10;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 1 - Wysupp;Loc G - Wysupp;10;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 1 - Wysupp;Loc H - Wysupp;10;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 2 - Wysupp;Loc E - Wysupp;20;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 2 - Wysupp;Loc F - Wysupp;20;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 2 - Wysupp;Loc G - Wysupp;20;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 2 - Wysupp;Loc H - Wysupp;20;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 5 - Wysupp;Loc E - Wysupp;11;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 5 - Wysupp;Loc F - Wysupp;11;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 5 - Wysupp;Loc G - Wysupp;11;WY_CAMP17;WY_CAMP17
WY_CAMP17;Prod 5 - Wysupp;Loc H - Wysupp;11;WY_CAMP17;WY_CAMP17
WY_CAMP18;Prod 2 - Wysupp;Loc E - Wysupp;20;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 2 - Wysupp;Loc F - Wysupp;20;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 2 - Wysupp;Loc G - Wysupp;20;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 2 - Wysupp;Loc H - Wysupp;20;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 3 - Wysupp;Loc E - Wysupp;7;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 3 - Wysupp;Loc F - Wysupp;7;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 3 - Wysupp;Loc G - Wysupp;7;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 3 - Wysupp;Loc H - Wysupp;7;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 4 - Wysupp;Loc E - Wysupp;1.8;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 4 - Wysupp;Loc F - Wysupp;1.8;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 4 - Wysupp;Loc G - Wysupp;1.8;WY_CAMP18;WY_CAMP18
WY_CAMP18;Prod 4 - Wysupp;Loc H - Wysupp;1.8;WY_CAMP18;WY_CAMP18
WY_CAMP19;Prod 1 - Wysupp;Loc E - Wysupp;10.5;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 1 - Wysupp;Loc F - Wysupp;10.5;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 1 - Wysupp;Loc G - Wysupp;10.5;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 1 - Wysupp;Loc H - Wysupp;10.5;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 4 - Wysupp;Loc E - Wysupp;1.9;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 4 - Wysupp;Loc F - Wysupp;1.9;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 4 - Wysupp;Loc G - Wysupp;1.9;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 4 - Wysupp;Loc H - Wysupp;1.9;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 5 - Wysupp;Loc E - Wysupp;11.5;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 5 - Wysupp;Loc F - Wysupp;11.5;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 5 - Wysupp;Loc G - Wysupp;11.5;WY_CAMP19;WY_CAMP19
WY_CAMP19;Prod 5 - Wysupp;Loc H - Wysupp;11.5;WY_CAMP19;WY_CAMP19
WY_CAMP20;Prod 4 - Wysupp;Loc E - Wysupp;1.9;WY_CAMP20;WY_CAMP20
WY_CAMP20;Prod 4 - Wysupp;Loc F - Wysupp;1.9;WY_CAMP20;WY_CAMP20
WY_CAMP20;Prod 4 - Wysupp;Loc G - Wysupp;1.9;WY_CAMP20;WY_CAMP20
WY_CAMP20;Prod 4 - Wysupp;Loc H - Wysupp;1.9;WY_CAMP20;WY_CAMP20
WY_CAMP20;Prod 5 - Wysupp;Loc E - Wysupp;11.5;WY_CAMP20;WY_CAMP20
WY_CAMP20;Prod 5 - Wysupp;Loc F - Wysupp;11.5;WY_CAMP20;WY_CAMP20
WY_CAMP20;Prod 5 - Wysupp;Loc G - Wysupp;11.5;WY_CAMP20;WY_CAMP20
WY_CAMP20;Prod 5 - Wysupp;Loc H - Wysupp;11.5;WY_CAMP20;WY_CAMP20
WY_CAMP21;Prod 4 - Wysupp;Loc E - Wysupp;1.9;WY_CAMP21;WY_CAMP21
WY_CAMP21;Prod 4 - Wysupp;Loc F - Wysupp;1.9;WY_CAMP21;WY_CAMP21
WY_CAMP21;Prod 4 - Wysupp;Loc G - Wysupp;1.9;WY_CAMP21;WY_CAMP21
WY_CAMP21;Prod 4 - Wysupp;Loc H - Wysupp;1.9;WY_CAMP21;WY_CAMP21
WY_CAMP21;Prod 5 - Wysupp;Loc E - Wysupp;11.5;WY_CAMP21;WY_CAMP21
WY_CAMP21;Prod 5 - Wysupp;Loc F - Wysupp;11.5;WY_CAMP21;WY_CAMP21
WY_CAMP21;Prod 5 - Wysupp;Loc G - Wysupp;11.5;WY_CAMP21;WY_CAMP21
WY_CAMP21;Prod 5 - Wysupp;Loc H - Wysupp;11.5;WY_CAMP21;WY_CAMP21
WY_CAMP22;Prod 1 - Wysupp;Loc E - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 1 - Wysupp;Loc F - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 1 - Wysupp;Loc G - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 1 - Wysupp;Loc H - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 2 - Wysupp;Loc E - Wysupp;23;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 2 - Wysupp;Loc F - Wysupp;23;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 2 - Wysupp;Loc G - Wysupp;23;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 2 - Wysupp;Loc H - Wysupp;23;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 3 - Wysupp;Loc E - Wysupp;8;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 3 - Wysupp;Loc F - Wysupp;8;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 3 - Wysupp;Loc G - Wysupp;8;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 3 - Wysupp;Loc H - Wysupp;8;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 4 - Wysupp;Loc E - Wysupp;2;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 4 - Wysupp;Loc F - Wysupp;2;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 4 - Wysupp;Loc G - Wysupp;2;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 4 - Wysupp;Loc H - Wysupp;2;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 5 - Wysupp;Loc E - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 5 - Wysupp;Loc F - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 5 - Wysupp;Loc G - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP22;Prod 5 - Wysupp;Loc H - Wysupp;12;WY_CAMP22;WY_CAMP22
WY_CAMP23;Prod 2 - Wysupp;Loc E - Wysupp;23;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 2 - Wysupp;Loc F - Wysupp;23;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 2 - Wysupp;Loc G - Wysupp;23;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 2 - Wysupp;Loc H - Wysupp;23;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 3 - Wysupp;Loc E - Wysupp;8;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 3 - Wysupp;Loc F - Wysupp;8;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 3 - Wysupp;Loc G - Wysupp;8;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 3 - Wysupp;Loc H - Wysupp;8;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 4 - Wysupp;Loc E - Wysupp;2;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 4 - Wysupp;Loc F - Wysupp;2;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 4 - Wysupp;Loc G - Wysupp;2;WY_CAMP23;WY_CAMP23
WY_CAMP23;Prod 4 - Wysupp;Loc H - Wysupp;2;WY_CAMP23;WY_CAMP23
WY_CAMP24;Prod 1 - Wysupp;Loc E - Wysupp;12;WY_CAMP24;WY_CAMP24
WY_CAMP24;Prod 1 - Wysupp;Loc F - Wysupp;12;WY_CAMP24;WY_CAMP24
WY_CAMP24;Prod 1 - Wysupp;Loc G - Wysupp;12;WY_CAMP24;WY_CAMP24
WY_CAMP24;Prod 1 - Wysupp;Loc H - Wysupp;12;WY_CAMP24;WY_CAMP24
WY_CAMP24;Prod 2 - Wysupp;Loc E - Wysupp;23;WY_CAMP24;WY_CAMP24
WY_CAMP24;Prod 2 - Wysupp;Loc F - Wysupp;23;WY_CAMP24;WY_CAMP24
WY_CAMP24;Prod 2 - Wysupp;Loc G - Wysupp;23;WY_CAMP24;WY_CAMP24
WY_CAMP24;Prod 2 - Wysupp;Loc H - Wysupp;23;WY_CAMP24;WY_CAMP24
WY_CAMP25;Prod 4 - Wysupp;Loc E - Wysupp;2;WY_CAMP25;WY_CAMP25
WY_CAMP25;Prod 4 - Wysupp;Loc F - Wysupp;2;WY_CAMP25;WY_CAMP25
WY_CAMP25;Prod 4 - Wysupp;Loc G - Wysupp;2;WY_CAMP25;WY_CAMP25
WY_CAMP25;Prod 4 - Wysupp;Loc H - Wysupp;2;WY_CAMP25;WY_CAMP25`
    },
    assortments: {
        name: 'assortments_yyyy-MM-dd.csv',
        content: `code;name;merchandising_product_group_5;assortment_type
Assortment1 - Wysupp;Assortment1 - Wysupp;P1004;ENTERPRISE
Assortment2 - Wysupp;Assortment2 - Wysupp;P1004;CLUSTER
Assortment3 - Wysupp;Assortment3 - Wysupp;P1004;LOCATION`
    },
    product_location_category_assortment: {
        name: 'product_location_catergory_assortments_yyyy-MM-dd.csv',
        content: `assortment;product;merchandising_product_group_5;location
Assortment1 - Wysupp;Prod1 - Wysupp;P1004;Loc1 - Wysupp
Assortment2 - Wysupp;Prod2 - Wysupp;P1005;Loc1 - Wysupp
Assortment3 - Wysupp;Prod3 - Wysupp;P1004;Loc1 - Wysupp`
    },
    category_clusters: [
        {
            name: 'clusters_yyyy-MM-dd.csv',
            content: `code;name
URBAN;Urban Store Format
RURAL;Rural Store Format
FLAGSHIP;Flagship Premium Format
LARGE;Large Volume Format
SMALL;Small Convenience Format`
        },
        {
            name: 'cluster_groups_yyyy-MM-dd.csv',
            content: `code;name;start_date;end_date
CG_Wysupp_2026;Wysupp Core Assortment 2026;2026-01-01;2026-12-31
CG_Wysupp_Promo;Wysupp Seasonal Clusters;2026-03-01;2026-05-31
CG_Wysupp_Urban;Wysupp Urban Strategy;;`
        },
        {
            name: 'cluster_group_clusters_yyyy-MM-dd.csv',
            content: `cluster_group;cluster
CG_Wysupp_2026;URBAN
CG_Wysupp_2026;RURAL
CG_Wysupp_2026;FLAGSHIP
CG_Wysupp_Promo;LARGE
CG_Wysupp_Urban;URBAN`
        },
        {
            name: 'location_category_cluster_group_cluster_yyyy-MM-dd.csv',
            content: `location;merchandising_product_group_x;cluster_group;name;cluster;name
Loc A - Wysupp;P1G1 - Wysupp;CG_Wysupp_2026;Wysupp Core Assortment 2026;URBAN;Urban Store Format
Loc B - Wysupp;P1G1 - Wysupp;CG_Wysupp_2026;Wysupp Core Assortment 2026;RURAL;Rural Store Format
Loc C - Wysupp;P4G4 - Wysupp;CG_Wysupp_Promo;Wysupp Seasonal Clusters;LARGE;Large Volume Format
Loc A - Wysupp;P2G3 - Wysupp;CG_Wysupp_Urban;Wysupp Urban Strategy;FLAGSHIP;Flagship Premium Format
Loc B - Wysupp;P3G3 - Wysupp;CG_Wysupp_2026;Wysupp Core Assortment 2026;RURAL;Rural Store Format`
        }
    ],
    floorplan_and_planogram_migrations: {
        name: 'planogram_assignments_yyyy-MM-dd.csv',
        content: `location;planogram_name;category_code
110293;Milk;2345432
110299;Juice;234555`
    },
    floor_plans: {
        name: 'floor_plans_yyyy-MM-dd.csv',
        content: `code;start_date;end_date;status
FP_LocA_Wysupp;2026-01-01;2026-12-31;Live
FP_LocB_Wysupp;2026-01-01;2026-12-31;Live
FP_LocC_Wysupp;2026-03-01;2026-12-31;Planned
FP_LocA_Archive;2025-01-01;2025-12-31;Archived
FP_Promo_Wysupp;2026-01-15;2026-02-15;Live`
    },
    location_category_spaces: [
        {
            name: 'location_category_spaces_yyyy-MM-dd.csv',
            content: `code;location;merchandising_product_group_x;floor_plan;start_date;end_date;base_depth;base_height;base_width;planogram_depth;planogram_height;planogram_width;number_of_bays;number_of_times_in_floor_plan
;Loc A - Wysupp;P1G1 - Wysupp;FP_LocA_Wysupp;;;0.6;0.1;1.0;0.5;2.2;1.0;2;1
;Loc A - Wysupp;P2G3 - Wysupp;FP_LocA_Wysupp;;;0.6;0.1;1.0;0.5;2.2;2.0;4;1
;Loc B - Wysupp;P1G1 - Wysupp;FP_LocB_Wysupp;;;0.8;0.15;1.2;0.6;2.4;1.2;3;1
;Loc B - Wysupp;P3G3 - Wysupp;FP_LocB_Wysupp;;;0.8;0.15;1.2;0.6;2.4;1.2;3;1
;Loc C - Wysupp;P4G4 - Wysupp;FP_LocC_Wysupp;;;0.6;0.1;1.0;0.5;2.2;1.0;2;1
LocA_P4G4_20260116;Loc A - Wysupp;P4G4 - Wysupp;;2026-01-16;2026-06-01;0.6;0.1;1.0;0.5;2.2;1.0;1;
LocB_P4G4_20260116;Loc B - Wysupp;P4G4 - Wysupp;;2026-01-16;2026-06-01;0.6;0.1;1.0;0.5;2.2;1.0;1;`
        },
        {
            name: 'location_category_space_bays_yyyy-MM-dd.csv',
            content: `location;merchandising_product_group_x;code;bay_index;floor_plan;location_category_space;base_depth;base_height;base_width;depth;height;width;notch_gap;notch_height;notch_number
Loc A - Wysupp;P1G1 - Wysupp;BAY_A1_1;1;FP_LocA_Wysupp;LocA_P1G1;0.6;0.1;1.0;0.5;2.2;1.0;0.05;0.1;20
Loc A - Wysupp;P1G1 - Wysupp;BAY_A1_2;2;FP_LocA_Wysupp;LocA_P1G1;0.6;0.1;1.0;0.5;2.2;1.0;0.05;0.1;20
Loc A - Wysupp;P2G3 - Wysupp;BAY_A2_1;1;FP_LocA_Wysupp;LocA_P2G3;0.6;0.1;1.0;0.5;2.2;1.0;0.05;0.1;20
Loc B - Wysupp;P1G1 - Wysupp;BAY_B1_1;1;FP_LocB_Wysupp;LocB_P1G1;0.8;0.15;1.2;0.6;2.4;1.2;0.05;0.1;22
Loc B - Wysupp;P1G1 - Wysupp;BAY_B1_2;2;FP_LocB_Wysupp;LocB_P1G1;0.8;0.15;1.2;0.6;2.4;1.2;0.05;0.1;22
Loc B - Wysupp;P1G1 - Wysupp;BAY_B1_3;3;FP_LocB_Wysupp;LocB_P1G1;0.8;0.15;1.2;0.6;2.4;1.2;0.05;0.1;22
Loc C - Wysupp;P4G4 - Wysupp;BAY_C4_1;1;FP_LocC_Wysupp;LocC_P4G4;0.6;0.1;1.0;0.5;2.2;1.0;0.05;0.1;20
Loc A - Wysupp;P4G4 - Wysupp;BAY_A4_1;1;;LocA_P4G4_20260116;0.6;0.1;1.0;0.5;2.2;1.0;;;`
        },
        {
            name: 'location_category_space_bay_components_yyyy-MM-dd.csv',
            content: `bay_index;code;component_index;depth;height;width
1;COMP_S1;1;0.5;0.02;1.0
1;COMP_S2;2;0.5;0.02;1.0
1;COMP_S3;3;0.5;0.02;1.0
2;COMP_S1;1;0.5;0.02;1.0
2;COMP_S2;2;0.5;0.02;1.0
3;COMP_S1;1;0.6;0.03;1.2
3;COMP_S2;2;0.6;0.03;1.2
1;COMP_H1;4;0.3;0.01;0.05
2;COMP_H1;3;0.3;0.01;0.05`
        }
    ],
};

// --- Helper Components --- (No changes to logic, styling updated in <style> tag)

const LoadingSpinner = () => <div className="loading-container"><div className="loading-spinner"></div></div>;
const ErrorMessage = ({ message }) => <div className="form-feedback error"><p className="error-title">An Error Occurred</p><p>{message}</p></div>;

const CollapsibleSection = ({ title, children, headerActions, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="info-section-card">
            <div className="info-section-header-flex">
                <button onClick={() => setIsOpen(!isOpen)} className="collapsible-button" style={{ flexGrow: 1, justifyContent: 'flex-start', padding: 0 }}>
                    <h2 className="info-section-title">{title}</h2>
                    <span className={`arrow ${isOpen ? 'open' : ''}`} style={{ marginLeft: '1rem' }}>&#9660;</span>
                </button>
                {headerActions && <div className="export-buttons">{headerActions}</div>}
            </div>
            {isOpen && (
                <div className="collapsible-content">
                    {children}
                </div>
            )}
        </div>
    );
};

// --- FIX: Dynamic Interface Summary Builder ---
const InterfaceSummary = ({ activeInterfaces, onExportAllCSV, onExportAllPDF }) => {
    const [isOpen, setIsOpen] = useState(true);
    const normalizeName = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

    // Map through the active interfaces and pull either the hardcoded summary, 
    // or build a default summary on the fly for custom interfaces.
    const combinedSummaries = useMemo(() => {
        return activeInterfaces.map(iface => {
            const normName = normalizeName(iface.interface_name);
            const hardcoded = allInterfaceSummaries.find(summary => normalizeName(summary.name) === normName);

            if (hardcoded) {
                return hardcoded; 
            }
            
            // Generate a summary automatically for custom interfaces
            return {
                name: iface.interface_name,
                type: 'Custom Interface',
                subType: iface.interface_type || 'Custom Data',
                cycle: 'As Scheduled',
                recommended: 'Project Specific'
            };
        });
    }, [activeInterfaces]);

    if (combinedSummaries.length === 0) return null;

    // Fixed formatName to also swap underscores for spaces so custom interfaces look pretty
    const formatName = (name) => name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

    return (
        <div className="info-section-card">
            <div className="info-section-header-flex">
                <button onClick={() => setIsOpen(!isOpen)} className="collapsible-button" style={{ flexGrow: 1, justifyContent: 'flex-start', padding: 0 }}>
                    <h2 className="info-section-title">Project Interface Summary</h2>
                    <span className={`arrow ${isOpen ? 'open' : ''}`}>&#9660;</span>
                </button>
                <div className="export-buttons">
                    <button onClick={onExportAllCSV} className="export-button csv">Export All (CSV)</button>
                    <button onClick={onExportAllPDF} className="export-button pdf">Export All (PDF)</button>
                </div>
            </div>
            {isOpen && (
                <div className="collapsible-content">
                    <div className="table-wrapper">
                        <table className="summary-table">
                            <thead>
                                <tr>
                                    <th>Interface Name</th>
                                    <th>Type</th>
                                    <th>Sub-Type</th>
                                    <th>Sending Cycle</th>
                                    <th>Recommended Data Content</th>
                                </tr>
                            </thead>
                            <tbody>
                                {combinedSummaries.map(item => (
                                    <tr key={item.name}>
                                        <td>{formatName(item.name)}</td>
                                        <td>{item.type}</td>
                                        <td>{item.subType}</td>
                                        <td>{item.cycle}</td>
                                        <td>{item.recommended}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

const DummyFilesModal = ({ isOpen, onClose, activeInterfaces }) => {
    const visibleFiles = useMemo(() => {
        if (!activeInterfaces || activeInterfaces.length === 0) {
            return {};
        }
        const activeKeys = new Set(activeInterfaces.map(iface => iface.interface_name));
        return Object.entries(dummyFiles).reduce((acc, [key, value]) => {
            const historicalKey = `historical_${key}`;
            if (activeKeys.has(key) || activeKeys.has(historicalKey)) {
                acc[key] = value;
            }
            return acc;
        }, {});
    }, [activeInterfaces]);

    const visibleFileKeys = useMemo(() => Object.keys(visibleFiles), [visibleFiles]);
    const [activeTab, setActiveTab] = useState('');
    const [activeSubFileIndex, setActiveSubFileIndex] = useState(0);

    useEffect(() => {
        if (isOpen && visibleFileKeys.length > 0) {
            if (!activeTab || !visibleFiles[activeTab]) {
                setActiveTab(visibleFileKeys[0]);
                setActiveSubFileIndex(0);
            }
        } else if (!isOpen) {
            setActiveTab('');
            setActiveSubFileIndex(0);
        }
    }, [isOpen, visibleFileKeys, activeTab, visibleFiles]);

    const handleTabChange = (key) => {
        setActiveTab(key);
        setActiveSubFileIndex(0);
    };

    if (!isOpen) return null;

    const getCurrentFileData = () => {
        const data = visibleFiles[activeTab];
        if (Array.isArray(data)) {
            return data[activeSubFileIndex];
        }
        return data;
    };

    const currentFileData = getCurrentFileData();

    const handleDownload = () => {
        const fileData = currentFileData;
        if (!fileData) return;
        const blob = new Blob([fileData.content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileData.name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const formatTabName = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Dummy File Examples</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {visibleFileKeys.length > 0 ? (
                        <>
                            <div className="dummy-file-tabs">
                                {visibleFileKeys.map(key => (
                                    <button
                                        key={key}
                                        className={`tab-button ${activeTab === key ? 'active' : ''}`}
                                        onClick={() => handleTabChange(key)}
                                    >
                                        {formatTabName(key)}
                                    </button>
                                ))}
                            </div>

                            {/* Sub-file selection for multiple files */}
                            {Array.isArray(visibleFiles[activeTab]) && (
                                <div className="dummy-file-subtabs" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    {visibleFiles[activeTab].map((file, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setActiveSubFileIndex(index)}
                                            className={`action-button ${activeSubFileIndex === index ? 'primary' : 'secondary'}`}
                                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}
                                        >
                                            {file.name}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="dummy-file-content">
                                <pre><code>{currentFileData ? currentFileData.content : ''}</code></pre>
                            </div>
                        </>
                    ) : (
                        <div className="info-message" style={{ padding: '2rem', textAlign: 'center' }}>
                            <p>No dummy files are available for the interfaces activated in this project.</p>
                        </div>
                    )}
                </div>
                {visibleFileKeys.length > 0 && currentFileData && (
                    <div className="modal-footer">
                        <button onClick={handleDownload} className="download-button">
                            Download {currentFileData.name}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
const GlossaryModal = ({ isOpen, onClose }) => { if (!isOpen) return null; return (<div className="modal-overlay" onClick={onClose}> <div className="modal-content" onClick={e => e.stopPropagation()}> <div className="modal-header"> <h3>Common Supply Chain Terms</h3> <button onClick={onClose} className="modal-close-button">&times;</button> </div> <div className="modal-body"> <dl className="glossary-list"> {supplyChainGlossary.map((item, index) => (<div key={`${item.term}-${index}`} className="glossary-item"> <dt className="glossary-term">{item.term}</dt> <dd className="glossary-description">{item.description}</dd> </div>))} </dl> </div> </div> </div>); };

const ChangeLogModal = ({ isOpen, onClose, interfaceName, changeLogs, isLoading, fetchError }) => {
    if (!isOpen) return null;
    const getChangeColor = (changeType) => {
        switch (changeType) {
            case 'Created': return 'log-created';
            case 'Updated': return 'log-updated';
            case 'Deleted': return 'log-deleted';
            default: return '';
        }
    };
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Change History for {interfaceName}</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {isLoading && <LoadingSpinner />}
                    {fetchError && <ErrorMessage message={fetchError} />}
                    {!isLoading && !fetchError && changeLogs.length === 0 && (
                        <div className="info-message">
                            <p>No change history found for this interface.</p>
                        </div>
                    )}
                    {!isLoading && !fetchError && changeLogs.length > 0 && (
                        <div className="log-list">
                            {changeLogs.map(log => (
                                <div key={log.id} className="log-entry">
                                    <div className="log-entry-header">
                                        <span className="log-user">{log.user_username}</span>
                                        <span className="log-timestamp">{new Date(log.timestamp).toLocaleString()}</span>
                                        <span className={`log-type-badge ${getChangeColor(log.change_type)}`}>{log.change_type}</span>
                                    </div>
                                    <div className="log-entry-body">
                                        <p><strong>Field:</strong> {log.db_field_name}</p>
                                        <p><strong>Changed:</strong> {log.changed_column}</p>
                                        {log.change_type === 'Updated' && (
                                            <div className="log-diff">
                                                <span className="old-value">{log.old_value || '""'}</span>
                                                <span className="arrow">→</span>
                                                <span className="new-value">{log.new_value || '""'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const RecentChangesModal = ({ isOpen, onClose, logs, isLoading, fetchError }) => {

    const groupedLogs = useMemo(() => {
        if (!logs || logs.length === 0) return [];

        const groups = logs.reduce((acc, log) => {
            const date = new Date(log.timestamp).toLocaleDateString('en-CA');
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(log);
            return acc;
        }, {});

        return Object.entries(groups).sort((a, b) => new Date(b[0]) - new Date(a[0]));
    }, [logs]);

    if (!isOpen) return null;

    const getChangeColor = (changeType) => {
        switch (changeType) {
            case 'Created': return 'log-created';
            case 'Updated': return 'log-updated';
            case 'Deleted': return 'log-deleted';
            default: return '';
        }
    };

    const formatInterfaceName = (name) => {
        if (typeof name !== 'string' || !name) return 'N/A';
        return name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>What's New? Recent Definition Changes</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {isLoading && <LoadingSpinner />}
                    {fetchError && <ErrorMessage message={fetchError} />}
                    {!isLoading && !fetchError && groupedLogs.length === 0 && (
                        <div className="info-message">
                            <p>No recent changes found for project interfaces.</p> 
                        </div>
                    )}
                    {!isLoading && !fetchError && groupedLogs.length > 0 && (
                        <div className="update-notes-container">
                            {groupedLogs.map(([date, dateLogs]) => (
                                <div key={date} className="update-note-group">
                                    <h4 className="update-note-date">
                                        Update - {new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}
                                    </h4>
                                    <div className="log-list">
                                        {dateLogs.map(log => (
                                            <div key={log.id} className="log-entry">
                                                <div className="log-entry-header">
                                                    <span className="log-user">{log.user_username}</span>
                                                    <span className="log-timestamp">{new Date(log.timestamp).toLocaleTimeString()}</span>
                                                    <span className={`log-type-badge ${getChangeColor(log.change_type)}`}>{log.change_type}</span>
                                                </div>
                                                <div className="log-entry-body">
                                                    <p><strong>Interface:</strong> {formatInterfaceName(log.interface_name)}</p>
                                                    <p><strong>Field:</strong> {log.db_field_name}</p>
                                                    <p><strong>Changed:</strong> {log.changed_column}</p>
                                                    {log.change_type === 'Updated' && (
                                                        <div className="log-diff">
                                                            <span className="old-value">{log.old_value || '""'}</span>
                                                            <span className="arrow">→</span>
                                                            <span className="new-value">{log.new_value || '""'}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const InterfaceCard = ({ iface, formatInterfaceName, onShowChangeLogs, onExportCSV, onExportPDF }) => {
    const [isOpen, setIsOpen] = useState(true);
    const formattedName = formatInterfaceName(iface.interface_name);

    return (
        <div className="content-card interface-card">
            <div className="interface-header">
                <button onClick={() => setIsOpen(!isOpen)} className="collapsible-button interface-title-button">
                    <h2 className="interface-title">{formattedName}</h2>
                    <span className={`arrow ${isOpen ? 'open' : ''}`}>&#9660;</span>
                </button>
                <div className="export-buttons">
                    <button onClick={onShowChangeLogs} className="export-button history">View Historical Changes</button>
                    <button onClick={onExportCSV} className="export-button csv">Export CSV</button>
                    <button onClick={onExportPDF} className="export-button pdf">Export PDF</button>
                </div>
            </div>
            {isOpen && (
                <>
                    <div className="interface-meta">
                        <span className="meta-item"><strong>Type:</strong> {iface.interface_type}</span>
                        {iface.project_drop_name && <span className="meta-item drop-item">{iface.project_drop_name}</span>}
                        {iface.description && <span className="meta-item"><strong>Notes:</strong> {iface.description}</span>}
                    </div>

                    <div className="source-files-container">
                        {Object.entries(iface.files).map(([filename, fields]) => (
                            <div key={filename} className="source-file-section">
                                <h3 className="source-file-title">{filename}</h3>
                                <div className="fields-grid">
                                    {fields.map(field => (
                                        <div key={field.db_field_name} className="field-card">
                                            <div className="field-card-header">
                                                <h4 className="field-name">{field.db_field_name}</h4>
                                                <div>
                                                    <span className={`status-tag ${field.is_mandatory ? 'mandatory' : 'optional'}`}>
                                                        {field.is_mandatory ? 'Mandatory' : 'Optional'}
                                                    </span>
                                                    {(field.project_id != null && field.is_custom) && (
                                                        <span className="status-tag custom">Custom</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="field-card-body">
                                                <div className="field-detail"><strong>File Header:</strong> {field.file_header_name || 'N/A'}</div>
                                                <div className="field-detail"><strong>Data Type:</strong> {field.data_type || 'N/A'}</div>
                                                <div className="field-detail"><strong>Table in RELEX:</strong> {field.table_in_relex || 'N/A'}</div>
                                                <div className="field-detail"><strong>UI Field in RELEX:</strong> {field.ui_field_in_relex || 'N/A'}</div>
                                                <div className="field-detail full-width"><strong>Used For:</strong> <p>{field.used_for || 'N/A'}</p></div>
                                                <div className="field-detail full-width"><strong>Description:</strong> <p>{field.description || 'N/A'}</p></div>
                                                <div className="field-detail full-width"><strong>Example:</strong> <p><code className="example-code">{field.example_value || 'N/A'}</code></p></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};


// --- Main Page Component ---
export default function InterfaceDefinitions() {
    const navigate = useNavigate();
    
    const { user, selectedProjectName } = useAuth();
    const userProjects = user?.projects || [];

    const [interfaceData, setInterfaceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingInterfaces, setLoadingInterfaces] = useState(false);
    const [error, setError] = useState(null);

    const [isDummyFilesModalOpen, setIsDummyFilesModalOpen] = useState(false);
    const [isGlossaryModalOpen, setIsGlossaryModalOpen] = useState(false);

    const [isChangeLogModalOpen, setIsChangeLogModalOpen] = useState(false);
    const [selectedInterfaceForLogs, setSelectedInterfaceForLogs] = useState('');
    const [changeLogs, setChangeLogs] = useState([]);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [logFetchError, setLogFetchError] = useState(null);

    const [isRecentChangesModalOpen, setIsRecentChangesModalOpen] = useState(false);
    const [recentChanges, setRecentChanges] = useState([]);
    const [isLoadingRecentChanges, setIsLoadingRecentChanges] = useState(false);
    const [recentChangesError, setRecentChangesError] = useState(null);

    useEffect(() => {
        if (user) {
            setLoading(false);
        } else {
            setLoading(true); 
        }
    }, [user]);

    useEffect(() => {
        if (!selectedProjectName) {
            setInterfaceData([]);
            setLoadingInterfaces(false);
            return;
        }
        const fetchInterfaceData = async () => {
            setLoadingInterfaces(true);
            setError(null);
            try {
                const response = await axiosInstance.get(`/api/projects/${selectedProjectName}/interface-definitions`);
                
                const normalizeName = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '');
                
                const sortedData = response.data.sort((a, b) => {
                    const indexA = allInterfaceSummaries.findIndex(summary => 
                        normalizeName(summary.name) === normalizeName(a.interface_name)
                    );
                    const indexB = allInterfaceSummaries.findIndex(summary => 
                        normalizeName(summary.name) === normalizeName(b.interface_name)
                    );
                    
                    const orderA = indexA !== -1 ? indexA : 999999;
                    const orderB = indexB !== -1 ? indexB : 999999;
                    
                    return orderA - orderB;
                });

                setInterfaceData(sortedData);
                setLoadingInterfaces(false); 
            } catch (err) {
                if (err.response?.status === 401) return;
                
                setError(err.response?.data?.detail || 'Failed to load interface definitions.');
                setInterfaceData([]);
                setLoadingInterfaces(false); 
            }
        };
        fetchInterfaceData();
    }, [selectedProjectName]);

    const handleShowChangeLogs = async (interfaceName, formattedName) => {
        setSelectedInterfaceForLogs(formattedName);
        setIsChangeLogModalOpen(true);
        setIsLoadingLogs(true);
        setLogFetchError(null);
        setChangeLogs([]);
        try {
            const response = await axiosInstance.get(`/api/interfaces/${interfaceName}/change-logs`);
            setChangeLogs(response.data);
            setIsLoadingLogs(false); 
        } catch (err) {
            if (err.response?.status === 401) return;
            
            setLogFetchError(err.response?.data?.detail || 'Failed to load change history.');
            setIsLoadingLogs(false); 
        }
    };

    const handleShowRecentChanges = async () => {
        setIsRecentChangesModalOpen(true);
        setIsLoadingRecentChanges(true);
        setRecentChangesError(null);
        setRecentChanges([]);
        try {
            const response = await axiosInstance.get('/api/interface-definitions/recent-changes');
            const activeInterfaceNames = new Set(interfaceData.map(iface => iface.interface_name));
            const filteredChanges = response.data.filter(log => activeInterfaceNames.has(log.interface_name));
            setRecentChanges(filteredChanges);
            setIsLoadingRecentChanges(false); 
        } catch (err) {
            if (err.response?.status === 401) return;
            
            setRecentChangesError(err.response?.data?.detail || 'Failed to load recent changes.');
            setIsLoadingRecentChanges(false); 
        }
    };

    const formatInterfaceName = (name) => {
        if (typeof name !== 'string' || !name) {
            return 'N/A';
        }
        return name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    const handleExportCSV = (iface) => { const headers = ["DB Field Name", "File Header Name", "Table in RELEX", "UI Field in RELEX", "Used For", "Data Type", "Mandatory", "Description", "Example"]; let allCsvContent = []; Object.entries(iface.files).forEach(([filename, fields]) => { allCsvContent.push(`"Source File:","${filename}"`); allCsvContent.push(headers.join(',')); const rows = fields.map(field => [`"${field.db_field_name || ''}"`, `"${field.file_header_name || ''}"`, `"${field.table_in_relex || ''}"`, `"${field.ui_field_in_relex || ''}"`, `"${(field.used_for || '').replace(/"/g, '""')}"`, `"${field.data_type || ''}"`, `"${field.is_mandatory ? 'Yes' : 'No'}"`, `"${(field.description || '').replace(/"/g, '""')}"`, `"${field.example_value || ''}"`].join(',')); allCsvContent.push(...rows); allCsvContent.push(''); }); const csvContent = allCsvContent.join('\n'); const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.setAttribute('download', `${iface.interface_name}_definition.csv`); document.body.appendChild(link); link.click(); document.body.removeChild(link); };
    const handleExportPDF = (iface) => { const doc = new jsPDF({ orientation: 'landscape' }); let startY = 22; doc.setFontSize(16); doc.text(`Interface Definition: ${formatInterfaceName(iface.interface_name)}`, 14, 16); Object.entries(iface.files).forEach(([filename, fields]) => { if (startY > 180) { doc.addPage(); startY = 22; } doc.setFontSize(12); doc.text(`Source File: ${filename}`, 14, startY); startY += 8; const tableHeaders = [ 'DB Field', 'File Header', 'Data Type', 'Table in RELEX', 'UI Field in RELEX', 'Used For', 'Description', 'Example', 'Mandatory' ]; const tableBody = fields.map(field => [ field.db_field_name || 'N/A', field.file_header_name || 'N/A', field.data_type || 'N/A', field.table_in_relex || 'N/A', field.ui_field_in_relex || 'N/A', field.used_for || 'N/A', field.description || 'N/A', field.example_value || 'N/A', field.is_mandatory ? 'Yes' : 'No' ]); autoTable(doc, { startY: startY, head: [tableHeaders], body: tableBody, theme: 'grid', headStyles: { fillColor: [41, 128, 185], fontSize: 8 }, styles: { fontSize: 7, cellPadding: 2 }, columnStyles: { 5: { cellWidth: 60 }, 6: { cellWidth: 60 }, 8: { halign: 'center' } }, didDrawPage: (data) => { startY = data.cursor.y + 10; } }); startY = doc.lastAutoTable.finalY + 15; }); doc.save(`${iface.interface_name}_definition.pdf`); };
    const handleExportAllCSV = () => { if (interfaceData.length === 0) { alert("No interface data available to export."); return; } const headers = ["DB Field Name", "File Header Name", "Table in RELEX", "UI Field in RELEX", "Used For", "Data Type", "Mandatory", "Description", "Example"]; let allCsvContent = []; interfaceData.forEach(iface => { allCsvContent.push(`"Interface Name:","${formatInterfaceName(iface.interface_name)}"`); Object.entries(iface.files).forEach(([filename, fields]) => { allCsvContent.push(`"Source File:","${filename}"`); allCsvContent.push(headers.join(',')); const rows = fields.map(field => [`"${field.db_field_name || ''}"`, `"${field.file_header_name || ''}"`, `"${field.table_in_relex || ''}"`, `"${field.ui_field_in_relex || ''}"`, `"${(field.used_for || '').replace(/"/g, '""')}"`, `"${field.data_type || ''}"`, `"${field.is_mandatory ? 'Yes' : 'No'}"`, `"${(field.description || '').replace(/"/g, '""')}"`, `"${field.example_value || ''}"`].join(',')); allCsvContent.push(...rows); allCsvContent.push(''); }); allCsvContent.push(''); }); const csvString = allCsvContent.join('\n'); const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.setAttribute('download', `${selectedProjectName}_all_definitions.csv`); document.body.appendChild(link); link.click(); document.body.removeChild(link); };
    const handleExportAllPDF = () => { if (interfaceData.length === 0) { alert("No interface data available to export."); return; } const doc = new jsPDF({ orientation: 'landscape' }); let startY = 22; doc.setFontSize(16); doc.text(`Project: ${selectedProjectName} - All Interface Definitions`, 14, 16); interfaceData.forEach((iface, index) => { if (index > 0) { startY += 5; doc.setDrawColor(226, 232, 240); doc.line(14, startY, 283, startY); startY += 10; } if (startY > 180) { doc.addPage(); startY = 22; } doc.setFontSize(14); doc.text(`Interface: ${formatInterfaceName(iface.interface_name)}`, 14, startY); startY += 8; Object.entries(iface.files).forEach(([filename, fields]) => { if (startY > 180) { doc.addPage(); startY = 22; } doc.setFontSize(10); doc.text(`Source File: ${filename}`, 14, startY); const tableHeaders = [ 'DB Field', 'File Header', 'Data Type', 'Table in RELEX', 'UI Field in RELEX', 'Used For', 'Description', 'Example', 'Mandatory' ]; const tableBody = fields.map(field => [ field.db_field_name || 'N/A', field.file_header_name || 'N/A', field.data_type || 'N/A', field.table_in_relex || 'N/A', field.ui_field_in_relex || 'N/A', field.used_for || 'N/A', field.description || 'N/A', field.example_value || 'N/A', field.is_mandatory ? 'Yes' : 'No' ]); autoTable(doc, { startY: startY + 4, head: [tableHeaders], body: tableBody, theme: 'grid', headStyles: { fillColor: [41, 128, 185], fontSize: 8 }, styles: { fontSize: 7, cellPadding: 2 }, columnStyles: { 5: { cellWidth: 60 }, 6: { cellWidth: 60 }, 8: { halign: 'center' } }, }); startY = doc.lastAutoTable.finalY; }); startY += 5; }); doc.save(`${selectedProjectName}_all_definitions.pdf`); };
    const handleExportDiscrepanciesCSV = () => { const headers = ["Discrepancy Type", "Description"]; const csvRows = commonDiscrepanciesData.map(item => `"${item.term.replace(/"/g, '""')}","${item.description.replace(/"/g, '""').replace(/\n/g, ' ')}"`); const csvContent = [headers.join(','), ...csvRows].join('\n'); const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }); const link = document.createElement('a'); const url = URL.createObjectURL(blob); link.setAttribute('href', url); link.setAttribute('download', 'common_validation_discrepancies.csv'); document.body.appendChild(link); link.click(); document.body.removeChild(link); };
    const handleExportDiscrepanciesPDF = () => { const doc = new jsPDF(); doc.text("Common Validation Discrepancies", 14, 16); autoTable(doc, { startY: 20, head: [['Discrepancy Type', 'Description']], body: commonDiscrepanciesData.map(item => [item.term, item.description]), styles: { fontSize: 9 }, headStyles: { fillColor: [41, 128, 185] }, columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 'auto' } } }); doc.save('common_validation_discrepancies.pdf'); };

    const [searchTerm, setSearchTerm] = useState('');

    const filteredInterfaces = useMemo(() => {
        if (!searchTerm) return interfaceData;
        return interfaceData.filter(iface => {
            const name = iface.interface_name || '';
            const formatted = name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
            return formatted.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [interfaceData, searchTerm]);

    if (loading) return <LoadingSpinner />;

    return (
        <div className="admin-page-container">
            <Header />

            <main className="admin-main-content">
            <div className="page-header">
                <button onClick={() => navigate('/home')} className="back-button">
                    <FaArrowLeft /> Back
                </button>
                
                <div className="header-title-group">
                    <h1 className="page-title">Interface Data Requisites</h1>
                    {selectedProjectName && (
                        <div className="project-context-inline">
                            <span className="separator">|</span>
                            <span className="context-label">Project:</span>
                            <span className="project-highlight">{selectedProjectName}</span>
                        </div>
                    )}
                </div>

                <div className="header-actions">
                    <button onClick={handleShowRecentChanges} className="action-button secondary">What's New</button>
                    <button onClick={() => setIsGlossaryModalOpen(true)} className="action-button secondary">Supply Chain Glossary</button>
                    <button onClick={() => setIsDummyFilesModalOpen(true)} className="action-button secondary">View Dummy Files</button>
                </div>
            </div>

                {!selectedProjectName && userProjects.length > 0 && (
                    <div className="info-message"><p>Please select a project from the header to view its definitions.</p></div>
                )}
                {!selectedProjectName && userProjects.length === 0 && (
                     <div className="info-message"><p>You are not assigned to any projects.</p></div>
                )}
                {selectedProjectName && (
                    <>
                        <CollapsibleSection title="General Instructions" defaultOpen>
                            <div className="definitions-list-stacked">
                                {generalInstructions.map((item) => (
                                    <div key={item.term} className="definition-item">
                                        <dt>{item.term}</dt>
                                        <dd style={{ whiteSpace: 'pre-wrap' }}>{item.description}</dd>
                                    </div>
                                ))}
                            </div>
                        </CollapsibleSection>

                        <CollapsibleSection title="Additional Information" >
                            <h3 className="sub-section-title">Data Types</h3>
                            <div className="table-wrapper">
                                <table className="summary-table styled-table">
                                    <thead><tr><th>Type</th><th>Description</th></tr></thead>
                                    <tbody>{dataTypesData.map(item => <tr key={item.type}><td>{item.type}</td><td>{item.description}</td></tr>)}</tbody>
                                </table>
                            </div>
                            <h3 className="sub-section-title">Transaction Quantity Sign</h3>
                            <div className="table-wrapper">
                                <table className="summary-table styled-table">
                                    <thead><tr><th>Transaction</th><th>Sign</th><th>Description</th></tr></thead>
                                    <tbody>{transactionQuantitySignData.map(item => <tr key={item.transaction}><td>{item.transaction}</td><td>{item.sign}</td><td>{item.description}</td></tr>)}</tbody>
                                </table>
                            </div>
                            <h3 className="sub-section-title">Units of Measure</h3>
                            <div className="table-wrapper">
                                <table className="summary-table styled-table">
                                    <thead><tr><th>Unit</th><th>Description</th></tr></thead>
                                    <tbody>{unitsOfMeasureData.map(item => <tr key={item.unit}><td>{item.unit}</td><td>{item.description}</td></tr>)}</tbody>
                                </table>
                            </div>
                        </CollapsibleSection>

                        <CollapsibleSection
                            title="Common Validation Discrepancies"
                            headerActions={
                                <>
                                    <button onClick={handleExportDiscrepanciesCSV} className="export-button csv">Export CSV</button>
                                    <button onClick={handleExportDiscrepanciesPDF} className="export-button pdf">Export PDF</button>
                                </>
                            }
                        >
                            <div className="definitions-list-stacked">
                                {commonDiscrepanciesData.map((item) => (
                                    <div key={item.term} className="definition-item">
                                        <dt>{item.term}</dt>
                                        <dd style={{ whiteSpace: 'pre-wrap' }}>{item.description}</dd>
                                    </div>
                                ))}
                            </div>
                        </CollapsibleSection>

                        <InterfaceSummary
                            activeInterfaces={interfaceData}
                            onExportAllCSV={handleExportAllCSV}
                            onExportAllPDF={handleExportAllPDF}
                        />

                        {loadingInterfaces ? (
                            <LoadingSpinner />
                        ) : error ? (
                            <ErrorMessage message={error} />
                        ) : interfaceData.length > 0 ? (
                            <>
                                <div className="search-container">
                                    <input 
                                        type="text" 
                                        placeholder="Search active interfaces..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="interface-search-input"
                                    />
                                </div>
                                
                                {filteredInterfaces.length > 0 ? (
                                    <div className="interfaces-list">
                                        {filteredInterfaces.map(iface => (
                                            <InterfaceCard
                                                key={iface.interface_name}
                                                iface={iface}
                                                formatInterfaceName={formatInterfaceName}
                                                onShowChangeLogs={() => handleShowChangeLogs(iface.interface_name, formatInterfaceName(iface.interface_name))}
                                                onExportCSV={() => handleExportCSV(iface)}
                                                onExportPDF={() => handleExportPDF(iface)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="info-message"><p>No interfaces match your search "{searchTerm}".</p></div>
                                )}
                            </>
                        ) : (
                            <div className="info-message"><p>No active interface definitions found for the selected project.</p></div>
                        )}
                    </>
                )}
            </main>

            <DummyFilesModal
                isOpen={isDummyFilesModalOpen}
                onClose={() => setIsDummyFilesModalOpen(false)}
                activeInterfaces={interfaceData}
            />
            <GlossaryModal
                isOpen={isGlossaryModalOpen}
                onClose={() => setIsGlossaryModalOpen(false)}
            />
            <ChangeLogModal
                isOpen={isChangeLogModalOpen}
                onClose={() => setIsChangeLogModalOpen(false)}
                interfaceName={selectedInterfaceForLogs}
                changeLogs={changeLogs}
                isLoading={isLoadingLogs}
                fetchError={logFetchError}
            />
            <RecentChangesModal
                isOpen={isRecentChangesModalOpen}
                onClose={() => setIsRecentChangesModalOpen(false)}
                logs={recentChanges}
                isLoading={isLoadingRecentChanges}
                fetchError={recentChangesError}
            />

            <style>{`
                 /* --- Dark Theme Variables --- */
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                    --blue-accent: #60a5fa; --blue-bg: rgba(59, 130, 246, 0.1);
                    --yellow-accent: #fcd34d; --yellow-bg: rgba(245, 158, 11, 0.1);
                }

                 /* --- Base Styles --- */
                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
                .admin-main-content { max-width: 1400px; margin: 2rem auto; padding: 0 2rem; }
                .page-header {
                    display: flex;
                    align-items: center; /* Vertically align items */
                    gap: 1rem; /* Space between button, title, actions */
                    margin-bottom: 2rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid var(--border-color);
                 }
                 .header-title-group {
                    display: flex;
                    align-items: center;
                    flex-grow: 1;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                    margin: 0;
                    line-height: 1;
                    white-space: nowrap;
                }

                .project-context-inline {
                    display: flex;
                    align-items: center;
                    font-size: 1rem;
                    color: var(--light-text);
                    font-weight: 500;
                    white-space: nowrap;
                }

                .separator {
                    margin-right: 1rem;
                    color: var(--border-color);
                    font-weight: 300;
                    font-size: 1.2rem;
                    position: relative;
                    top: -1px;
                }

                .context-label {
                    margin-right: 0.5rem;
                }

                .project-highlight {
                    color: var(--green-accent);
                    font-weight: 600;
                }
                 .header-actions {
                    display: flex;
                    gap: 0.75rem;
                    margin-left: auto; /* Push actions to the right */
                    flex-shrink: 0; /* Prevent shrinking */
                 }

                .back-button {
                    background: var(--card-bg);
                    color: var(--text-color);
                    border: 1px solid var(--border-color);
                    padding: 0.5rem 1rem; /* Adjust padding as needed */
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-shrink: 0; /* Prevent shrinking */
                 }

                 /* --- Card Styles --- */
                .info-section-card { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 12px; padding: 1.5rem 2rem; margin-bottom: 2rem; }
                .content-card { /* General card style used by InterfaceCard */
                    background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 12px;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1); overflow: hidden;
                 }
                .info-section-header-flex { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 1px solid var(--border-color); }
                .info-section-title { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin: 0; padding: 0; border: none; }
                .collapsible-button { display: flex; align-items: center; background: none; border: none; padding: 0; margin: 0; cursor: pointer; text-align: left; color: inherit; font: inherit; }
                .collapsible-button .arrow { font-size: 1.5rem; color: var(--light-text); transition: transform 0.3s ease; margin-left: 1rem; }
                .collapsible-button .arrow.open { transform: rotate(180deg); }
                .collapsible-content { padding-top: 1rem; margin-top: 1rem; border-top: 1px solid var(--border-color); }
                .sub-section-title { font-size: 1.1rem; font-weight: 600; color: var(--light-text); margin-top: 1.5rem; margin-bottom: 0.75rem; }

                 /* --- List and Definition Styles --- */
                .definitions-list-stacked { display: flex; flex-direction: column; gap: 1rem; }
                .definition-item { padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
                .definition-item:last-child { border-bottom: none; padding-bottom: 0; }
                .definition-item dt { font-weight: 600; color: var(--light-text); margin-bottom: 0.25rem; }
                .definition-item dd { color: var(--text-color); margin: 0; font-size: 0.95rem; white-space: normal; overflow-wrap: break-word; line-height: 1.4; }

                 /* --- Table Styles --- */
                 .table-wrapper { overflow-x: auto; margin-bottom: 1rem; }
                 .summary-table, .styled-table {
                     width: 100%; border-collapse: collapse; word-break: normal; 
                     background-color: var(--input-bg); 
                     border: 1px solid var(--border-color);
                     border-radius: 8px; 
                     overflow: hidden; 
                  }
                 .summary-table th, .summary-table td,
                 .styled-table th, .styled-table td {
                     text-align: left; padding: 0.8rem 1rem; border-bottom: 1px solid var(--border-color);
                     white-space: normal; overflow-wrap: break-word; 
                     vertical-align: top; 
                 }
                 .summary-table thead, .styled-table thead {
                     background-color: var(--card-bg); 
                     position: sticky; top: 0; 
                     z-index: 10;
                  }
                 .summary-table th, .styled-table th { font-size: 0.8rem; color: var(--light-text); font-weight: 600; text-transform: uppercase; }
                 .summary-table td, .styled-table td { font-size: 0.9rem; color: var(--text-color); line-height: 1.4; }
                 .summary-table tbody tr:last-child td,
                 .styled-table tbody tr:last-child td { border-bottom: none; }
                 .summary-table tbody tr:hover,
                 .styled-table tbody tr:hover { background-color: #374151; }


                 /* --- Interface Card Styles --- */
                 .interfaces-list { display: grid; gap: 2.5rem; margin-top: 2rem; }
                 .interface-header { display: flex; justify-content: space-between; align-items: center; padding: 0 1.5rem; background-color: var(--dark-bg); border-bottom: 1px solid var(--border-color); }
                 .interface-title { font-size: 1.75rem; font-weight: 700; color: var(--text-color); margin: 0; padding: 1.5rem 0; }
                 .interface-title-button { flex-grow: 1; padding-right: 1rem; }
                 .interface-meta { padding: 1rem 1.5rem; font-size: 0.9rem; color: var(--light-text); display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem; border-bottom: 1px solid var(--border-color); }
                 .meta-item strong { color: var(--text-color); }
                 .drop-item { background-color: var(--blue-bg); color: var(--blue-accent); padding: 0.25rem 0.75rem; border-radius: 9999px; font-weight: 500; }
                 .source-files-container { padding: 1.5rem; display: flex; flex-direction: column; gap: 2rem; }
                 .source-file-section { background-color: var(--dark-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1.5rem; }
                 .source-file-title { font-size: 1.2rem; font-weight: 600; color: var(--light-text); margin: 0 0 1rem 0; padding-bottom: 0.75rem; border-bottom: 1px solid var(--border-color); }
                 .fields-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
                 .field-card { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 8px; overflow: hidden; } /* Rounded inner card */
                 .field-card-header { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1rem; border-bottom: 1px solid var(--border-color); background-color: var(--dark-bg); }
                 .field-name { font-size: 1.1rem; font-weight: 600; color: var(--text-color); margin: 0; }
                 .field-card-body { padding: 1rem; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; font-size: 0.9rem; }
                 .field-detail {
                    color: var(--light-text);
                    overflow-wrap: break-word; /* Allows breaking long words to prevent overflow */
                    word-break: break-word;    /* Alternative/fallback word breaking */
                    min-width: 0;              /* Helps grid/flex items shrink properly */
                }
                 .field-detail strong { color: var(--text-color); display: block; margin-bottom: 0.25rem; font-weight: 500; }
                 .field-detail p {
                    margin: 0;
                    white-space: pre-wrap;
                    color: var(--text-color);
                    line-height: 1.4;
                    overflow-wrap: break-word; /* Ensure wrapping within the paragraph too */
                    word-break: break-word;    /* Fallback */
                }
                 .example-code {
                    background-color: var(--dark-bg);
                    padding: 0.1rem 0.4rem;
                    border-radius: 4px;
                    font-family: monospace;
                    color: var(--text-color);
                    border: 1px solid var(--border-color);
                    white-space: pre-wrap;     /* Allow wrapping */
                    word-break: break-all;     /* Force break even for long unbreakable strings */
                    display: inline-block;     /* Ensures background covers wrapped lines */
                    max-width: 100%;         /* Prevent exceeding parent width */
                }
                 .field-detail.full-width { grid-column: 1 / -1; }

                 /* --- Status Tag Styles --- */
                 .status-tag { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.8rem; font-weight: 600; text-transform: capitalize; white-space: nowrap; }
                 .status-tag.custom { background-color: var(--blue-bg); color: var(--blue-accent); margin-left: 0.5rem; }
                 .status-tag.mandatory { background-color: var(--error-bg); color: var(--error-color); }
                 .status-tag.optional { background-color: var(--border-color); color: var(--text-color); }

                 /* --- Button Styles --- */
                .action-button { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; gap: 0.5rem; }
                .action-button.primary { background-color: var(--green-accent); color: var(--dark-bg); }
                .action-button.primary:hover:not(:disabled) { background-color: #38a169; }
                .action-button.secondary { background-color: var(--border-color); color: var(--text-color); }
                .action-button.secondary:hover { background-color: #5a6578; }
                .action-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }

                .export-buttons { display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0; }
                .export-button {
                    font-size: 0.8rem; padding: 0.4rem 0.8rem; border-radius: 6px; border: 1px solid var(--border-color);
                    background-color: var(--card-bg); color: var(--light-text); cursor: pointer; transition: all 0.2s;
                    display: inline-flex; align-items: center; gap: 0.3rem;
                }
                .export-button:hover { border-color: var(--green-accent); color: var(--green-accent); background-color: var(--dark-bg); }

                 /* --- Modal Styles (Dark Theme) --- */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 1000; padding: 1rem; }
                .modal-content { background: var(--card-bg); border: 1px solid var(--border-color); padding: 0; border-radius: 8px; width: 90%; max-width: 800px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
                .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid var(--border-color); }
                .modal-header h3 { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin: 0; }
                .modal-close-button { background: transparent; border: none; font-size: 1.5rem; cursor: pointer; color: var(--light-text); padding: 0 0.5rem; }
                .modal-body { overflow-y: auto; padding: 1.5rem; color: var(--text-color); }
                .modal-body::-webkit-scrollbar { width: 8px; }
                .modal-body::-webkit-scrollbar-track { background: var(--input-bg); }
                .modal-body::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }
                .modal-body::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }
                .modal-footer { display: flex; justify-content: flex-end; gap: 0.75rem; padding: 1rem 1.5rem; border-top: 1px solid var(--border-color); }
                .download-button { background-color: var(--green-accent); color: var(--dark-bg); padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
                .download-button:hover { background-color: #38a169; }

                 /* Dummy Files Modal Specific */
                .dummy-file-tabs { 
                    display: flex; 
                    border-bottom: 1px solid var(--border-color); 
                    margin-bottom: 1rem; 
                    overflow-x: auto; 
                    white-space: nowrap;
                    padding-bottom: 4px;
                    scroll-behavior: smooth;
                }
                .dummy-file-tabs::-webkit-scrollbar {
                    height: 6px;
                }
                .dummy-file-tabs::-webkit-scrollbar-track {
                    background: var(--input-bg);
                    border-radius: 4px;
                }
                .dummy-file-tabs::-webkit-scrollbar-thumb {
                    background-color: var(--border-color);
                    border-radius: 4px;
                }
                .dummy-file-tabs::-webkit-scrollbar-thumb:hover {
                    background-color: var(--light-text);
                }
                .tab-button { 
                    background: none; 
                    border: none; 
                    padding: 0.75rem 1rem; 
                    cursor: pointer; 
                    font-size: 0.9rem; 
                    color: var(--light-text); 
                    border-bottom: 2px solid transparent; 
                    text-transform: capitalize; 
                    flex-shrink: 0; 
                }
                .tab-button.active { 
                    color: var(--green-accent); 
                    border-bottom-color: var(--green-accent); 
                    font-weight: 600; 
                }
                .dummy-file-content { background-color: var(--dark-bg); border-radius: 4px; border: 1px solid var(--border-color); padding: 1rem; max-height: 40vh; overflow: auto; }
                .dummy-file-content pre { margin: 0; font-size: 0.85rem; color: var(--text-color); }

                 /* Glossary Modal Specific */
                .glossary-list { display: flex; flex-direction: column; gap: 1.25rem; }
                .glossary-item { padding-bottom: 1.25rem; border-bottom: 1px solid var(--border-color); }
                .glossary-item:last-child { border-bottom: none; }
                .glossary-term { font-weight: 600; color: var(--text-color); font-size: 1rem; margin-bottom: 0.25rem; }
                .glossary-description { color: var(--light-text); margin: 0; font-size: 0.95rem; line-height: 1.6; }

                 /* Change Log Modal Specific */
                .log-list { display: flex; flex-direction: column; gap: 1rem; }
                .log-entry { border: 1px solid var(--border-color); border-radius: 8px; background-color: var(--dark-bg); overflow: hidden; }
                .log-entry-header { display: flex; align-items: center; gap: 1rem; padding: 0.75rem 1rem; border-bottom: 1px solid var(--border-color); background-color: var(--input-bg); }
                .log-user { font-weight: 600; color: var(--text-color); }
                .log-timestamp { font-size: 0.8rem; color: var(--light-text); }
                .log-type-badge { margin-left: auto; font-size: 0.75rem; font-weight: 700; padding: 0.2rem 0.6rem; border-radius: 99px; }
                 /* Use dark theme badge colors */
                .log-created { background-color: var(--success-bg); color: var(--success-color); }
                .log-updated { background-color: var(--yellow-bg); color: var(--yellow-accent); }
                .log-deleted { background-color: var(--error-bg); color: var(--error-color); }
                .log-entry-body { padding: 1rem; font-size: 0.9rem; }
                .log-entry-body p { margin: 0 0 0.5rem 0; color: var(--light-text); }
                .log-entry-body p strong { color: var(--text-color); }
                .log-diff { display: flex; align-items: center; gap: 0.5rem; background-color: var(--input-bg); padding: 0.5rem; border-radius: 4px; border: 1px solid var(--border-color); }
                .old-value { text-decoration: line-through; color: var(--error-color); }
                .new-value { font-weight: 600; color: var(--success-color); }
                .arrow { color: var(--light-text); }

                 /* Recent Changes Modal Specific */
                 .update-notes-container { display: flex; flex-direction: column; gap: 2rem; }
                 .update-note-group { }
                 .update-note-date { font-size: 1.1rem; font-weight: 600; color: var(--light-text); padding-bottom: 0.75rem; margin-bottom: 1rem; border-bottom: 1px solid var(--border-color); }

                 /* --- Utility Styles --- */
                 .loading-container { display:flex; flex-direction:column; align-items:center; justify-content:center; padding: 3rem; color:var(--light-text); }
                 .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                 @keyframes spin { to { transform: rotate(360deg); } }

                 .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); padding: 1.5rem; border-radius: 8px; border: 1px solid var(--danger-color); }
                 .error-title { font-weight: 700; margin-bottom: 0.5rem; }

                 .info-message {
                     background-color: var(--card-bg);
                     border: 1px solid var(--border-color);
                     color: var(--light-text);
                     padding: 1.5rem;
                     border-radius: 8px;
                     text-align: center;
                     margin-top: 2rem;
                 }

                .dummy-file-content {
                    /* Ensure container padding doesn't conflict */
                    padding-right: 0 !important; 
                }

                .dummy-file-content pre {
                    /* Apply comfortable padding directly to the text element */
                    padding-right: 1.5rem; 
                    margin: 0;
                    /* Ensures padding is respected even if it scrolls horizontally */
                    width: fit-content;
                    min-width: 100%;
                    box-sizing: border-box;
                }
                .dummy-file-content::-webkit-scrollbar {
                    width: 8px;
                    height: 8px; /* Height for horizontal scrollbar */
                }
                .dummy-file-content::-webkit-scrollbar-track {
                    background: var(--input-bg);
                    border-radius: 4px;
                }
                .dummy-file-content::-webkit-scrollbar-thumb {
                    background-color: var(--border-color);
                    border-radius: 4px;
                    border: 1px solid var(--input-bg); /* Adds a little padding effect */
                }
                .dummy-file-content::-webkit-scrollbar-thumb:hover {
                    background-color: var(--light-text);
                }

                /* --- Search Bar Styles --- */
                .search-container { margin-bottom: 2rem; }
                .interface-search-input {
                    width: 100%;
                    padding: 1rem 1.5rem;
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 8px;
                    color: var(--text-color);
                    font-size: 1rem;
                    transition: all 0.2s ease;
                }
                .interface-search-input:focus {
                    outline: none;
                    border-color: var(--green-accent);
                    box-shadow: 0 0 0 1px var(--green-accent);
                }

                 /* Responsive Adjustments */
                @media (max-width: 768px) {
                    .admin-main-content { padding: 0 1rem; }
                    .page-header {
                        flex-direction: row; /* Keep row layout */
                        flex-wrap: wrap; /* Allow wrapping */
                        align-items: center; /* Center items vertically */
                        gap: 1rem; /* Maintain gap */
                    }
                    .back-button {
                        order: 1; /* Place back button first */
                    }
                     .page-title {
                         order: 2; /* Place title second */
                         flex-basis: 100%; /* Make title take full width if needed */
                         text-align: center; /* Center title on small screens */
                         margin-top: 0.5rem; /* Add space above title if wrapped */
                         flex-grow: 0; /* Prevent title from growing excessively */
                     }
                    .header-actions {
                        order: 3; /* Place actions last */
                        width: 100%;
                        justify-content: center; /* Center buttons */
                        margin-left: 0; /* Reset margin */
                        margin-top: 1rem; /* Add space above actions if wrapped */
                    }
                    .info-section-header-flex { flex-direction: column; align-items: flex-start; gap: 0.5rem; }
                    .export-buttons { margin-top: 0.5rem; }
                    .interface-header { flex-direction: column; align-items: flex-start; padding: 1rem 1.5rem; }
                    .interface-title-button { width: 100%; justify-content: space-between; } /* Make title button full width */
                    .interface-title { padding: 0; }
                    .fields-grid { grid-template-columns: 1fr; }
                    .field-card-body { grid-template-columns: 1fr; } /* Stack details on small screens */
                    .modal-content { max-width: 95%; }
                }
            `}</style>
        </div>
    );
}